import React, { useState, useRef, useEffect, useMemo, lazy, Suspense } from 'react';
import { useAppSelector } from '../../store/hooks';
import {
  useLazyRechercherCopieParCodeQuery,
  useAssocierCopieMutation,
  useDissocierCopieMutation,
  useGetCopiesExamensQuery,
  useGetStatsCopiesQuery,
  useLazyCompterOrphelinesQuery,
  usePurgerOrphelinesMutation,
  useGetHistoriqueAssociationsQuery,
  useAttribuerEnseignantMutation,
  useRetirerEnseignantMutation,
  useTransfererCopiesMutation,
  useGetStatsMatiereEnseignantQuery,
  useGetCopiesSansFormateurQuery,
  useGetHistoriqueStatsQuery,
} from '../../services/copiesApi';
import { useGetEtudiantsQuery } from '../../services/etudiantsApi';
import { useGetMatieresQuery } from '../../services/matieresApi';
import { useGetFormateursQuery, useGetAttributionsAnneeQuery } from '../../services/formateursApi';
import { useGetAnneeActiveQuery } from '../../services/anneeAcademiqueApi';
import { useGetClassesQuery } from '../../services/polesOptionsApi';
import { useGetAssocVerrousQuery } from '../../services/coreApi';
import DissocierLotModal from './DissocierLotModal';
import TransfererMatiereModal from './TransfererMatiereModal';
import ProgressionEDTModal from './ProgressionEDTModal';
import { RepartitionMini } from '../../components/ui/RepartitionMini';
import {
  Scan,
  UserCheck,
  CheckCircle2,
  XCircle,
  History,
  Search,
  BookOpen,
  Printer,
  FileSpreadsheet,
  BarChart3,
  Users,
  FileText,
  Download,
  Filter,
  RefreshCw,
  Unlink,
  X,
  ChevronDown,
  ChevronRight,
  Calendar,
  TrendingUp,
  PieChart,
  Clock,
  Lock,
  Unlock,
  AlertTriangle,
  Trash2,
  Info,
  Camera,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';

const BarcodeScanner = lazy(() => import('../../components/BarcodeScanner'));

// Types
interface Etudiant {
  id: number;
  matricule: string;
  nom: string;
  prenoms: string;
  prenom?: string;
  niveau: string;
  option?: string;
  classe?: string;
}

interface Matiere {
  id: number;
  code_matiere: string;
  nom_matiere: string;
  option?: string | null;
  classe?: string | null;
  niveau?: string;
}

interface HistoriqueItem {
  id: number;
  copie_code: string;
  action: string;
  matiere_nom?: string;
  matiere_code?: string;
  ancien_etudiant_matricule?: string;
  ancien_etudiant_nom?: string;
  nouvel_etudiant_matricule?: string;
  nouvel_etudiant_nom?: string;
  nouvel_etudiant_classe?: string;
  effectue_par_nom: string;
  date_action: string;
  commentaire?: string;
  adresse_ip?: string;
}

// Composant Autocomplete pour la recherche d'étudiants
interface AutocompleteEtudiantProps {
  etudiants: Etudiant[];
  value: string;
  onChange: (value: string) => void;
  onSelect: (etudiant: Etudiant) => void;
  disabled?: boolean;
  placeholder?: string;
  matiereOption?: string | null; // Option de la matière sélectionnée pour filtrer
  inputRef?: React.RefObject<HTMLInputElement | null>; // Pour le focus externe (ex: depuis le scan)
  onEnter?: () => void; // Callback quand Entrée est pressée
}

const AutocompleteEtudiant: React.FC<AutocompleteEtudiantProps> = ({
  etudiants,
  value,
  onChange,
  onSelect,
  disabled,
  placeholder,
  matiereOption,
  inputRef: externalRef,
  onEnter,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredEtudiants, setFilteredEtudiants] = useState<Etudiant[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Tous les étudiants accessibles (pas de filtrage par option pour éviter les incohérences de nommage)
  const etudiantsFiltresParOption = etudiants;

  useEffect(() => {
    if (showDropdown) {
      // Afficher tous les étudiants filtrés par option
      setFilteredEtudiants(etudiantsFiltresParOption.slice(0, 50));
      setIsOpen(true);
    } else if (value.length >= 1) {
      const searchTerm = value.toLowerCase();
      const terms = searchTerm.split(/\s+/).filter(Boolean);
      const filtered = etudiantsFiltresParOption.filter(e => {
        const fullName = `${e.nom} ${e.prenoms || ''} ${(e as any).prenom || ''}`.toLowerCase();
        const fullText = `${e.matricule} ${fullName} ${e.classe || ''}`.toLowerCase();
        // Tous les termes doivent matcher quelque part dans le texte complet
        return terms.every(t => fullText.includes(t));
      }).slice(0, 15);
      setFilteredEtudiants(filtered);
      setIsOpen(filtered.length > 0);
    } else {
      setFilteredEtudiants([]);
      setIsOpen(false);
    }
  }, [value, etudiantsFiltresParOption, showDropdown]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleDropdown = () => {
    if (disabled) return;
    setShowDropdown(!showDropdown);
    if (!showDropdown) {
      setIsOpen(true);
    }
  };

  return (
    <div ref={wrapperRef} className="relative flex-1">
      <div className="flex">
        <input
          ref={externalRef}
          type="text"
          value={value}
          onChange={(e) => {
            onChange(e.target.value.toUpperCase());
            setShowDropdown(false);
          }}
          onFocus={() => {
            if (value.length >= 1 && filteredEtudiants.length > 0) setIsOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && onEnter) onEnter();
          }}
          disabled={disabled}
          placeholder={placeholder}
          className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 text-lg font-mono disabled:bg-gray-100 dark:disabled:bg-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
        />
        <button
          type="button"
          onClick={handleToggleDropdown}
          disabled={disabled}
          className={`px-3 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r-md ${
            disabled ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer'
          }`}
          title="Afficher la liste des étudiants"
        >
          <ChevronDown size={20} className={`text-gray-500 dark:text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {isOpen && filteredEtudiants.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg dark:shadow-gray-900/50 max-h-72 overflow-auto">
          {matiereOption && (
            <div className="px-3 py-2 bg-blue-50 dark:bg-blue-900/30 border-b border-blue-100 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-300">
              Étudiants filtrés par option: <strong>{matiereOption}</strong> ({etudiantsFiltresParOption.length} étudiants)
            </div>
          )}
          {filteredEtudiants.map((etudiant) => (
            <div
              key={etudiant.id}
              onClick={() => {
                onSelect(etudiant);
                onChange(etudiant.matricule);
                setIsOpen(false);
                setShowDropdown(false);
              }}
              className="px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0"
            >
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-mono font-bold text-blue-600 dark:text-blue-400">{etudiant.matricule}</span>
                  <span className="mx-2 text-gray-700 dark:text-gray-300">-</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{etudiant.nom} {etudiant.prenoms || etudiant.prenom}</span>
                </div>
                <span className="text-xs text-white bg-blue-600 px-2 py-1 rounded font-semibold">
                  {etudiant.classe || `${etudiant.niveau}${etudiant.option || ''}`}
                </span>
              </div>
            </div>
          ))}
          {filteredEtudiants.length >= 50 && (
            <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 text-xs text-gray-500 dark:text-gray-400 text-center">
              Utilisez la recherche pour affiner les résultats...
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Type pour matière groupée (plusieurs options partageant la même matière)
interface MatiereGroupe {
  nom_matiere: string;
  niveau: string;
  matieres: Matiere[]; // Toutes les matières avec ce nom + niveau
  classes: string[]; // Ex: ['L1MI', 'L1EM', 'L1MT']
}

// Composant Autocomplete pour les matières (groupées par nom)
interface AutocompleteMatiereProps {
  matieres: Matiere[];
  value: string; // nom_matiere sélectionné (pas l'ID)
  onChange: (value: string) => void;
  onSelect: (groupe: MatiereGroupe) => void;
  disabled?: boolean;
  selectedNom?: string; // nom affiché
}

const AutocompleteMatiere: React.FC<AutocompleteMatiereProps> = ({
  matieres,
  value,
  onChange,
  onSelect,
  disabled,
  selectedNom,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Grouper les matières par nom + niveau
  const groupes = useMemo(() => {
    const map = new Map<string, MatiereGroupe>();
    matieres.forEach((m) => {
      const key = `${m.niveau || ''}|${m.nom_matiere}`;
      if (!map.has(key)) {
        map.set(key, {
          nom_matiere: m.nom_matiere,
          niveau: m.niveau || '',
          matieres: [],
          classes: [],
        });
      }
      const g = map.get(key)!;
      g.matieres.push(m);
      const classeRaw = m.classe || (m.option ? `${m.niveau || ''}${m.option}` : m.niveau || '');
      // Nettoyer les doublons du suffixe -CS répétés (bug d'import : `L2RT-CS-CS`)
      const classe = classeRaw.replace(/(-CS)(-CS)+/gi, '-CS');
      if (classe && !g.classes.includes(classe)) {
        g.classes.push(classe);
      }
    });
    // Trier par niveau puis nom
    return Array.from(map.values()).sort((a, b) => {
      if (a.niveau !== b.niveau) return a.niveau.localeCompare(b.niveau);
      return a.nom_matiere.localeCompare(b.nom_matiere);
    });
  }, [matieres]);

  const filteredGroupes = useMemo(() => {
    if (searchTerm.length >= 1) {
      const term = searchTerm.toLowerCase();
      return groupes.filter(g =>
        g.nom_matiere.toLowerCase().includes(term) ||
        g.classes.some(c => c.toLowerCase().includes(term)) ||
        g.niveau.toLowerCase().includes(term) ||
        g.matieres.some(m => m.code_matiere.toLowerCase().includes(term))
      ).slice(0, 50);
    }
    return groupes; // Afficher tout — regroupé par niveau dans le rendu
  }, [searchTerm, groupes]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={wrapperRef} className="relative flex-1">
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-md cursor-pointer flex items-center justify-between ${
          disabled ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-800 hover:border-blue-400 dark:hover:border-blue-500'
        }`}
      >
        <span className={selectedNom ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'}>
          {selectedNom || '-- Sélectionner ou rechercher une matière --'}
        </span>
        <ChevronDown size={20} className={`text-gray-400 dark:text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg dark:shadow-gray-900/50">
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher par nom de matière ou classe..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
              autoFocus
            />
          </div>
          <div className="max-h-80 overflow-auto">
            {filteredGroupes.length > 0 ? (
              filteredGroupes.map((groupe, idx) => {
                const isSelected = value === `${groupe.niveau}|${groupe.nom_matiere}`;
                const prevNiveau = idx > 0 ? filteredGroupes[idx - 1].niveau : null;
                const showHeader = groupe.niveau !== prevNiveau;
                return (
                  <React.Fragment key={`${groupe.niveau}|${groupe.nom_matiere}`}>
                    {showHeader && (
                      <div className="px-4 py-1.5 bg-gray-100 dark:bg-gray-700 text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wide sticky top-0">
                        {groupe.niveau || 'Sans niveau'}
                      </div>
                    )}
                    <div
                      onClick={() => {
                        onSelect(groupe);
                        onChange(`${groupe.niveau}|${groupe.nom_matiere}`);
                        setIsOpen(false);
                        setSearchTerm('');
                      }}
                      className={`px-4 py-2.5 hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                        isSelected ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-gray-900 dark:text-gray-100 font-medium text-sm">{groupe.nom_matiere}</span>
                        <div className="flex gap-1 flex-wrap justify-end ml-2">
                          {groupe.classes.map(c => (
                            <span
                              key={c}
                              className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded text-xs font-semibold"
                            >
                              {c}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })
            ) : (
              <div className="px-4 py-3 text-gray-500 dark:text-gray-400 text-center">
                Aucune matière trouvée
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Modal Component
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, size = 'lg' }) => {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl',
    full: 'max-w-full mx-4'
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-80" onClick={onClose} />
        <div className={`inline-block w-full ${sizeClasses[size]} my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl dark:shadow-gray-900/50 rounded-lg`}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300">
              <X size={24} />
            </button>
          </div>
          <div className="px-6 py-4 max-h-[80vh] overflow-y-auto">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

const AssociationCopies: React.FC = () => {
  const currentUser = useAppSelector(state => state.auth.user);
  const [codeCopie, setCodeCopie] = useState('');
  const [matriculeEtudiant, setMatriculeEtudiant] = useState('');
  const [selectedMatiere, setSelectedMatiere] = useState(''); // format: "niveau|nom_matiere"
  const [selectedMatiereGroupe, setSelectedMatiereGroupe] = useState<MatiereGroupe | null>(null);
  const [modeAssociation, setModeAssociation] = useState<'SCAN' | 'MANUEL'>('MANUEL');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [copieInfo, setCopieInfo] = useState<any>(null);
  const [etudiantInfo, setEtudiantInfo] = useState<Etudiant | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAnimation, setConfirmAnimation] = useState<'idle' | 'enter' | 'success'>('idle');
  const confirmRef = useRef<HTMLDivElement>(null);
  const [selectedEnseignantId, setSelectedEnseignantId] = useState<number | null>(null);
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const [showAttribFormateur, setShowAttribFormateur] = useState(false);
  const [showSansFormateur, setShowSansFormateur] = useState(false);
  const [sansFormateurFiltre, setSansFormateurFiltre] = useState<{ niveau?: string; pole?: string }>({});
  const [sansFormateurSearch, setSansFormateurSearch] = useState('');
  const [expandedMatiere, setExpandedMatiere] = useState<number | null>(null);

  // États pour les modales
  const [showHistorique, setShowHistorique] = useState(false);
  const [showAnalyse, setShowAnalyse] = useState(false);
  const [showListeAssociees, setShowListeAssociees] = useState(false);
  const [showProgressionEDT, setShowProgressionEDT] = useState(false);
  const [showDissocierLot, setShowDissocierLot] = useState(false);
  const [showTransfererMatiere, setShowTransfererMatiere] = useState(false);
  const [warningData, setWarningData] = useState<any>(null);
  // Fix 2026-09-01 : erreur affichée directement DANS le dialog de
  // confirmation (au lieu d'un toast en dehors). Permet à l'opérateur
  // de voir immédiatement pourquoi l'association a échoué + décider.
  const [dialogError, setDialogError] = useState<null | {
    type?: string; message: string; details?: Record<string, any>;
    canForce?: boolean;
  }>(null);

  // Verrou de la matière : quand activé, la matière ne change pas entre deux copies
  const [matiereVerrouilee, setMatiereVerrouilee] = useState(false);
  // Mode session : NORMAL (ordinaire) ou RATTRAPAGE — sert à filtrer/avertir
  // l'agent en cas de mismatch avec le code de copie scanné.
  const [sessionMode, setSessionMode] = useState<'NORMAL' | 'RATTRAPAGE'>('NORMAL');
  // Parité du semestre : 'impair' = S1/S3/S5 ; 'pair' = S2/S4/S6.
  // Évite les confusions du type "Mécanique des Fluides I (S1) vs II (S2)".
  // Persisté en localStorage pour éviter de re-choisir à chaque session.
  const [semestreParity, setSemestreParity] = useState<'impair' | 'pair'>(
    () => (localStorage.getItem('assoc_semestre_parity') as 'impair' | 'pair') || 'impair'
  );
  useEffect(() => {
    localStorage.setItem('assoc_semestre_parity', semestreParity);
  }, [semestreParity]);

  // Verrous admin — bloque les toggles session/parité pour éviter les erreurs
  // des agents pendant une session ciblée (ex : ordinaire S2 uniquement).
  const { data: assocVerrous } = useGetAssocVerrousQuery();
  const verrouSession = assocVerrous?.session_mode || '';   // '' | 'NORMAL' | 'RATTRAPAGE'
  const verrouParity = assocVerrous?.semestre_parity || ''; // '' | 'impair' | 'pair'
  useEffect(() => {
    // Applique le verrou dès qu'il est lu — force la valeur.
    if (verrouSession && verrouSession !== sessionMode) setSessionMode(verrouSession as 'NORMAL' | 'RATTRAPAGE');
    if (verrouParity && verrouParity !== semestreParity) setSemestreParity(verrouParity as 'impair' | 'pair');
    // Volontairement pas de dep sur sessionMode/semestreParity : on veut juste
    // forcer une fois quand la valeur du verrou change (ou arrive du back).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verrouSession, verrouParity]);

  const inputRef = useRef<HTMLInputElement>(null);
  const etudiantInputRef = useRef<HTMLInputElement>(null);

  const [rechercherCopie, { isLoading: rechercheLoading }] = useLazyRechercherCopieParCodeQuery();
  const [associerCopie, { isLoading: associationLoading }] = useAssocierCopieMutation();
  const [dissocierCopie] = useDissocierCopieMutation();
  const { data: etudiants = [], refetch: refetchEtudiants, isFetching: fetchingEtudiants } = useGetEtudiantsQuery();
  const { data: matieres = [], refetch: refetchMatieres, isFetching: fetchingMatieres } = useGetMatieresQuery();
  const { data: formateurs = [] } = useGetFormateursQuery({ actif: true });
  const [attribuerEnseignant, { isLoading: attribLoading }] = useAttribuerEnseignantMutation();
  const [retirerEnseignant, { isLoading: retirerLoading }] = useRetirerEnseignantMutation();
  const [transfererCopies] = useTransfererCopiesMutation();
  const { data: statsFormateur, refetch: refetchStatsFormateur } = useGetStatsMatiereEnseignantQuery(undefined, { skip: !showAttribFormateur });
  const { data: dataSansFormateur, refetch: refetchSansFormateur, isFetching: fetchingSansFormateur } = useGetCopiesSansFormateurQuery(sansFormateurFiltre, { skip: !showSansFormateur });
  const [selectedClassesForAttrib, setSelectedClassesForAttrib] = useState<Record<string, string[]>>({});
  const { data: allClassesBDD = [] } = useGetClassesQuery({ actif: true });
  const [addClasseOpen, setAddClasseOpen] = useState<number | null>(null);
  const [addClasseSearch, setAddClasseSearch] = useState('');
  const [attribMatiereSearch, setAttribMatiereSearch] = useState('');
  const [attribFiltreProblemes, setAttribFiltreProblemes] = useState(false);

  // Fusionner les matières par nom (même nom = même ligne avec toutes les classes)
  const matieresFusionnees = useMemo(() => {
    if (!statsFormateur?.matieres) return [];
    const map: Record<string, {
      matiere_nom: string;
      matiere_ids: number[];
      matiere_codes: string[];
      total: number;
      avec_enseignant: number;
      sans_enseignant: number;
      classes: Array<{ classe: string; count: number }>;
      enseignants: Array<{ id: number; nom: string; count: number }>;
      conflit_formateurs: boolean;
    }> = {};

    for (const mat of statsFormateur.matieres) {
      const key = mat.matiere_nom;
      if (!map[key]) {
        map[key] = {
          matiere_nom: mat.matiere_nom,
          matiere_ids: [],
          matiere_codes: [],
          total: 0,
          avec_enseignant: 0,
          sans_enseignant: 0,
          classes: [],
          enseignants: [],
          conflit_formateurs: false,
        };
      }
      const entry = map[key];
      entry.matiere_ids.push(mat.matiere_id);
      if (!entry.matiere_codes.includes(mat.matiere_code)) entry.matiere_codes.push(mat.matiere_code);
      entry.total += mat.total;
      entry.avec_enseignant += mat.avec_enseignant;
      entry.sans_enseignant += mat.sans_enseignant;

      // Fusionner classes
      for (const c of (mat.classes || [])) {
        const existing = entry.classes.find(ec => ec.classe === c.classe);
        if (existing) existing.count += c.count;
        else entry.classes.push({ ...c });
      }

      // Fusionner enseignants
      for (const e of mat.enseignants) {
        const existing = entry.enseignants.find(ee => ee.id === e.id);
        if (existing) existing.count += e.count;
        else entry.enseignants.push({ ...e });
      }
    }

    // Détecter les conflits : même classe avec plusieurs formateurs différents
    for (const entry of Object.values(map)) {
      if (entry.enseignants.length > 1) {
        entry.conflit_formateurs = true;
      }
    }

    return Object.values(map).sort((a, b) => {
      if (a.sans_enseignant > 0 && b.sans_enseignant === 0) return -1;
      if (a.sans_enseignant === 0 && b.sans_enseignant > 0) return 1;
      return a.matiere_nom.localeCompare(b.matiere_nom);
    });
  }, [statsFormateur]);
  const { data: anneeActive } = useGetAnneeActiveQuery();
  const { data: attributionsAnnee } = useGetAttributionsAnneeQuery(
    { annee_academique: anneeActive?.libelle ?? '' },
    { skip: !anneeActive }
  );
  const { data: statsCopies, refetch: refetchStats } = useGetStatsCopiesQuery();
  const [compterOrphelines, { data: orphelinesData }] = useLazyCompterOrphelinesQuery();
  const [purgerOrphelines, { isLoading: purgeLoading }] = usePurgerOrphelinesMutation();
  const { data: copiesAssociees, refetch: refetchAssociees, isFetching: fetchingAssociees } = useGetCopiesExamensQuery({ est_associee: true }, { skip: !showListeAssociees });
  const [histPage, setHistPage] = useState(1);
  const [histSearch, setHistSearch] = useState('');
  const [histAction, setHistAction] = useState('');
  const [histDateDebut, setHistDateDebut] = useState('');
  const [histDateFin, setHistDateFin] = useState('');
  const [histMatiere, setHistMatiere] = useState('');
  const [histUtilisateur, setHistUtilisateur] = useState('');
  const [histClasse, setHistClasse] = useState('');
  const [histFormateur, setHistFormateur] = useState('');
  const { data: historiqueData, refetch: refetchHistorique, isFetching: fetchingHistorique } = useGetHistoriqueAssociationsQuery(
    {
      page: histPage,
      page_size: 50,
      search: histSearch || undefined,
      action: histAction || undefined,
      date_debut: histDateDebut || undefined,
      date_fin: histDateFin || undefined,
      matiere: histMatiere || undefined,
      utilisateur: histUtilisateur || undefined,
      classe: histClasse || undefined,
      formateur: histFormateur || undefined,
    },
    { skip: !showHistorique }
  );
  const { data: historiqueStats } = useGetHistoriqueStatsQuery(undefined, { skip: !showHistorique });

  // Convertir les données en tableaux
  const etudiantsArray = Array.isArray(etudiants) ? etudiants : [];
  // Filtrage par parité du semestre : impair (S1/S3/S5) vs pair (S2/S4/S6).
  // Évite que l'agent associe par erreur une copie de "Mécanique I" (S1) à
  // "Mécanique II" (S2) — bug réel constaté en délibération.
  const matieresArray = useMemo(() => {
    const arr = Array.isArray(matieres) ? matieres : [];
    return arr.filter((m: any) => {
      const sem = Number(m.semestre);
      if (!sem) return true; // matière sans semestre : on garde par sécurité
      const estPaire = sem % 2 === 0;
      return semestreParity === 'pair' ? estPaire : !estPaire;
    });
  }, [matieres, semestreParity]);
  const historiqueArray = historiqueData?.results || [];
  const historiqueCount = historiqueData?.count || 0;
  const historiqueTotalPages = Math.ceil(historiqueCount / 50);
  const copiesAssocieesArray = Array.isArray(copiesAssociees) ? copiesAssociees : [];

  // Classes du groupe filtrées par le formateur sélectionné :
  // ne garder que celles où ce formateur intervient réellement (via
  // MatiereEnseignant → attributionsAnnee). Évite d'associer un étudiant
  // à une classe/formateur qui ne lui fait pas cours. Les doublons de
  // suffixe (-CS-CS, bug d'import historique) sont nettoyés.
  const classesFormateurAffichage = useMemo(() => {
    if (!selectedMatiereGroupe) return [] as string[];
    const raw = selectedMatiereGroupe.matieres
      .filter(m => {
        // Si aucun formateur sélectionné → laisser passer (comportement pré-fix)
        if (!selectedEnseignantId || !attributionsAnnee) return true;
        const attr = attributionsAnnee[String(m.id)];
        return attr && attr.formateur_id === selectedEnseignantId;
      })
      .map(m => m.classe || (m.option ? `${m.niveau || ''}${m.option}` : m.niveau || ''))
      .filter(Boolean)
      // Retirer les doublons du suffixe -CS répétés (`L2RT-CS-CS` → `L2RT-CS`)
      .map(c => c.replace(/(-CS)(-CS)+/gi, '-CS'));
    // Dédupliquer en conservant l'ordre
    return Array.from(new Set(raw));
  }, [selectedMatiereGroupe, selectedEnseignantId, attributionsAnnee]);

  // Résoudre la matière exacte pour l'étudiant sélectionné (dans le groupe)
  const resolvedMatiereObj = useMemo(() => {
    if (!selectedMatiereGroupe) return null;
    // Si un seul choix dans le groupe, c'est lui
    if (selectedMatiereGroupe.matieres.length === 1) return selectedMatiereGroupe.matieres[0];
    // Sinon résoudre par l'option de l'étudiant
    if (etudiantInfo) {
      const etudOption = etudiantInfo.option?.toLowerCase();
      const etudClasse = etudiantInfo.classe?.toLowerCase();
      const match = selectedMatiereGroupe.matieres.find(m => {
        if (!m.option) return false;
        const mOpt = m.option.toLowerCase();
        return etudOption === mOpt || (etudClasse && etudClasse.includes(mOpt));
      });
      if (match) return match;
    }
    // Fallback : première matière du groupe
    return selectedMatiereGroupe.matieres[0];
  }, [selectedMatiereGroupe, etudiantInfo]);

  // Pour la compatibilité avec le code existant
  const selectedMatiereObj = resolvedMatiereObj;

  // Vrai s'il y a une incompatibilité entre l'option de l'étudiant et celle de la matière
  const optionMismatch = useMemo(() => {
    if (!etudiantInfo || !selectedMatiereGroupe) return false;
    // Si le groupe ne contient aucune matière avec l'option de l'étudiant
    const etudOption = etudiantInfo.option?.toLowerCase();
    const etudClasse = etudiantInfo.classe?.toLowerCase();
    const hasMatch = selectedMatiereGroupe.matieres.some(m => {
      if (!m.option) return true; // matière sans option = compatible avec tous
      const mOpt = m.option.toLowerCase();
      return etudOption === mOpt || (etudClasse && etudClasse.includes(mOpt));
    });
    return !hasMatch;
  }, [etudiantInfo, selectedMatiereGroupe]);

  // Focus sur l'input copie au chargement (mode scan prêt)
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Rechercher automatiquement la copie quand le code est saisi (tous modes)
  useEffect(() => {
    // Délai pour éviter trop de requêtes (debounce)
    const delai = modeAssociation === 'SCAN' ? 100 : 500;

    if (codeCopie && codeCopie.length >= 6) {
      const timer = setTimeout(() => {
        handleRechercherCopie();
      }, delai);

      return () => clearTimeout(timer);
    }
  }, [codeCopie]);

  const handleRechercherCopie = async () => {
    if (!codeCopie) return;

    // Nettoyer le code (supprimer espaces et caractères invisibles du lecteur)
    const codeNettoye = codeCopie.trim().replace(/[\r\n\t]/g, '');

    try {
      const copie = await rechercherCopie(codeNettoye).unwrap();
      setCopieInfo(copie);

      // Vérifier la cohérence entre le mode session et le type_examen de la copie
      const copieType = (copie as any).type_examen || 'NORMAL';
      if (copieType !== sessionMode) {
        setMessage({
          type: 'error',
          text: copieType === 'RATTRAPAGE'
            ? `⚠ Cette copie est de type RATTRAPAGE (code ${codeNettoye}) mais vous êtes en mode "Session ordinaire". Basculez en mode "Session rattrapage" en haut de la page.`
            : `⚠ Cette copie est de type ${copieType} mais vous êtes en mode "${sessionMode === 'RATTRAPAGE' ? 'Session rattrapage' : 'Session ordinaire'}". Basculez de mode si vous voulez l'associer.`,
        });
        return;
      }

      if (copie.est_associee) {
        setMessage({
          type: 'error',
          text: `Cette copie est déjà associée à ${copie.etudiant_nom} (${copie.etudiant_matricule})`,
        });
      } else {
        setMessage(null);
        // Auto-sélectionner la matière de la copie SEULEMENT si le verrou est désactivé
        if (!matiereVerrouilee && copie.matiere) {
          // Trouver la matière dans le tableau et reconstruire le groupe
          const mat = matieresArray.find(m => m.id === copie.matiere);
          if (mat) {
            const key = `${mat.niveau || ''}|${mat.nom_matiere}`;
            setSelectedMatiere(key);
            // Construire le groupe à partir de toutes les matières avec ce nom + niveau
            const groupeMatieres = matieresArray.filter(m => m.nom_matiere === mat.nom_matiere && (m.niveau || '') === (mat.niveau || ''));
            const classesSet = groupeMatieres.map(m => m.classe || (m.option ? `${m.niveau || ''}${m.option}` : m.niveau || '')).filter(Boolean);
            const classes = classesSet.filter((c, i) => classesSet.indexOf(c) === i);
            setSelectedMatiereGroupe({
              nom_matiere: mat.nom_matiere,
              niveau: mat.niveau || '',
              matieres: groupeMatieres,
              classes,
            });
          }
        }
        // Transférer le focus vers le champ étudiant dès la copie trouvée (tous modes)
        setTimeout(() => etudiantInputRef.current?.focus(), 80);
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.data?.error || 'Copie introuvable',
      });
      setCopieInfo(null);
    }
  };

  const handleSelectEtudiant = (etudiant: Etudiant) => {
    setEtudiantInfo(etudiant);
    setMatriculeEtudiant(etudiant.matricule);
    // Bloquer si type_examen de la copie ne correspond pas au mode session
    const copieType = (copieInfo as any)?.type_examen || 'NORMAL';
    if (copieInfo && copieType !== sessionMode) {
      setMessage({
        type: 'error',
        text: copieType === 'RATTRAPAGE'
          ? `⛔ Cette copie est de type RATTRAPAGE. Basculez en mode "Session rattrapage" pour l'associer.`
          : `⛔ Cette copie est de type ${copieType}. Basculez en mode "Session ordinaire" pour l'associer.`,
      });
      return;
    }
    // Ouvrir la boîte de confirmation si copie et matière sont déjà remplies
    if (codeCopie && selectedMatiereGroupe && !copieInfo?.est_associee) {
      setConfirmAnimation('enter');
      setShowConfirmDialog(true); setDialogError(null);
      setTimeout(() => confirmRef.current?.focus(), 50);
    }
  };

  const handleSelectMatiereGroupe = (groupe: MatiereGroupe) => {
    setSelectedMatiereGroupe(groupe);
    setSelectedMatiere(`${groupe.niveau}|${groupe.nom_matiere}`);
    // Auto-sélectionner le formateur si un seul est attribué à cette matière
    if (attributionsAnnee && groupe.matieres.length > 0) {
      const firstMat = groupe.matieres[0];
      const attr = attributionsAnnee[String(firstMat.id)];
      if (attr) {
        setSelectedEnseignantId(attr.formateur_id);
      } else {
        setSelectedEnseignantId(null);
      }
    } else {
      setSelectedEnseignantId(null);
    }
  };

  const handleAssocier = async () => {
    if (!codeCopie || !matriculeEtudiant || !selectedMatiereGroupe) {
      setMessage({
        type: 'error',
        text: 'Veuillez remplir tous les champs obligatoires : code copie, matricule étudiant et matière',
      });
      return;
    }

    if (copieInfo?.est_associee) {
      setMessage({
        type: 'error',
        text: 'Cette copie est déjà associée',
      });
      return;
    }

    // BLOQUER si le type_examen de la copie ne correspond pas au mode session
    const copieType = (copieInfo as any)?.type_examen || 'NORMAL';
    if (copieType !== sessionMode) {
      setMessage({
        type: 'error',
        text: copieType === 'RATTRAPAGE'
          ? `⛔ Association refusée : la copie ${codeCopie} est de type RATTRAPAGE. Basculez en mode "Session rattrapage" en haut de la page.`
          : `⛔ Association refusée : la copie ${codeCopie} est de type ${copieType}. Basculez en mode "Session ordinaire".`,
      });
      // Fermer aussi la modal de confirmation si ouverte
      setShowConfirmDialog(false);
      return;
    }

    // Avertissement si incompatibilité d'option (mais ne bloque plus l'association)
    // Les incohérences de nommage (ex: "Transport" vs "Transit") ne doivent pas empêcher l'association

    // Résoudre l'ID exact de la matière à partir du groupe + option de l'étudiant
    let matiereIdResolu = resolvedMatiereObj?.id;
    // Fallback: si pas de match par option, prendre la première matière du groupe
    if (!matiereIdResolu && selectedMatiereGroupe?.matieres.length) {
      matiereIdResolu = selectedMatiereGroupe.matieres[0].id;
    }
    if (!matiereIdResolu) {
      setMessage({
        type: 'error',
        text: 'Impossible de résoudre la matière. Veuillez en sélectionner une.',
      });
      return;
    }

    try {
      const result = await associerCopie({
        code_copie: codeCopie,
        matricule_etudiant: matriculeEtudiant,
        matiere_id: matiereIdResolu,
        enseignant_id: selectedEnseignantId || undefined,
        mode_association: modeAssociation,
        session_mode: sessionMode,
      }).unwrap();

      // Animation de succès dans la boîte de confirmation
      setConfirmAnimation('success');
      setMessage({
        type: 'success',
        text: result.message || 'Association réussie !',
      });

      // Attendre l'animation puis réinitialiser
      setTimeout(() => {
        setShowConfirmDialog(false);
        setConfirmAnimation('idle');
        setCodeCopie('');
        setMatriculeEtudiant('');
        setCopieInfo(null);
        setEtudiantInfo(null);
        if (!matiereVerrouilee) {
          setSelectedMatiere('');
          setSelectedMatiereGroupe(null);
        }
        refetchStats();
        // Focus retour immédiat sur le champ code copie
        inputRef.current?.focus();
      }, 800);
    } catch (error: any) {
      const data = error?.data;
      // Fix 2026-09-01 : afficher TOUS les warnings/erreurs backend directement
      // DANS le dialog de confirmation (au lieu de fermer et toast externe).
      // Le backend renvoie maintenant des `warning:true, type:'...', details:{...}`
      // pour : niveau_mismatch, matiere_non_programme, copie_deja_associee_autre_etudiant,
      // double_composition, formateur_mismatch.
      if (data?.warning || data?.message || data?.error) {
        setDialogError({
          type: data?.type,
          message: data?.message || data?.error || 'Erreur lors de l\'association',
          details: data?.details,
          canForce: !!data?.force_url,
        });
      } else {
        setMessage({
          type: 'error',
          text: 'Erreur lors de l\'association',
        });
      }
    }
  };

  // Fix 2026-09-01 : force depuis le dialog (pour les cas warning force_url=true)
  const handleForceInDialog = async () => {
    if (!dialogError) return;
    try {
      const result = await associerCopie({
        code_copie: codeCopie,
        matricule_etudiant: matriculeEtudiant,
        matiere_id: (selectedMatiereGroupe?.matieres[0]?.id ?? undefined) as any,
        enseignant_id: selectedEnseignantId || undefined,
        mode_association: modeAssociation,
        force: true,
      }).unwrap();
      setDialogError(null);
      setConfirmAnimation('success');
      setMessage({ type: 'success', text: result.message || 'Association forcée' });
      setTimeout(() => {
        setShowConfirmDialog(false);
        setConfirmAnimation('idle');
        setCodeCopie('');
        setMatriculeEtudiant('');
        setCopieInfo(null);
        setEtudiantInfo(null);
        refetchStats();
        inputRef.current?.focus();
      }, 800);
    } catch (err: any) {
      const data = err?.data;
      setDialogError({
        type: data?.type,
        message: data?.message || data?.error || 'Erreur lors du force',
        details: data?.details,
        canForce: !!data?.force_url,
      });
    }
  };

  const handleForceAssociation = async () => {
    if (!warningData) return;
    try {
      const result = await associerCopie({
        code_copie: warningData.codeCopie,
        matricule_etudiant: warningData.matriculeEtudiant,
        matiere_id: warningData.matiereIdResolu,
        enseignant_id: warningData.enseignantId || undefined,
        mode_association: warningData.modeAssociation,
        force: true,
      }).unwrap();
      setWarningData(null);
      setConfirmAnimation('success');
      setMessage({ type: 'success', text: `${result.message} (force malgre incoherence)` });
      setTimeout(() => {
        setShowConfirmDialog(false);
        setConfirmAnimation('idle');
        setCodeCopie('');
        setMatriculeEtudiant('');
        setCopieInfo(null);
        setEtudiantInfo(null);
        refetchStats();
        inputRef.current?.focus();
      }, 800);
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.data?.message || 'Erreur' });
    }
  };

  const handleDissocier = async (codeCopie: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir dissocier cette copie ?')) return;

    try {
      await dissocierCopie({ code_copie: codeCopie }).unwrap();
      setMessage({ type: 'success', text: 'Copie dissociée avec succès' });

      // Réinitialiser les infos de la copie si c'est celle affichée
      if (copieInfo?.code_copie === codeCopie) {
        setCopieInfo(null);
        setCodeCopie('');
        setMatriculeEtudiant('');
        if (!matiereVerrouilee) {
          setSelectedMatiere('');
          setSelectedMatiereGroupe(null);
        }
        setEtudiantInfo(null);
      }

      refetchStats();
      refetchHistorique();
      refetchAssociees();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.data?.message || error?.data?.error || 'Erreur lors de la dissociation',
      });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, field: 'copie' | 'etudiant') => {
    if (e.key === 'Enter') {
      if (field === 'copie') {
        handleRechercherCopie();
      } else if (field === 'etudiant') {
        handleAssocier();
      }
    }
  };

  // Statistiques pour l'analyse
  const statsData = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const thisWeekStart = new Date();
    thisWeekStart.setDate(thisWeekStart.getDate() - 7);

    const associationsAujourdhui = historiqueArray.filter(h =>
      h.date_action.startsWith(today) && h.action === 'ASSOCIATION'
    ).length;

    const associationsSemaine = historiqueArray.filter(h => {
      const date = new Date(h.date_action);
      return date >= thisWeekStart && h.action === 'ASSOCIATION';
    }).length;

    // Stats par matière
    const parMatiere: Record<string, number> = {};
    copiesAssocieesArray.forEach((c: any) => {
      const nom = c.matiere_nom || 'Non définie';
      parMatiere[nom] = (parMatiere[nom] || 0) + 1;
    });

    // Stats par jour (7 derniers jours)
    const parJour: Record<string, number> = {};
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      parJour[dateStr] = 0;
    }
    historiqueArray.forEach(h => {
      if (h.action === 'ASSOCIATION') {
        const dateStr = h.date_action.split('T')[0];
        if (parJour[dateStr] !== undefined) {
          parJour[dateStr]++;
        }
      }
    });

    // Stats par mode
    const parMode: Record<string, number> = { SCAN: 0, MANUEL: 0, IMPORT: 0 };
    copiesAssocieesArray.forEach((c: any) => {
      const mode = c.mode_association || 'MANUEL';
      parMode[mode] = (parMode[mode] || 0) + 1;
    });

    return {
      totalCopies: statsCopies?.total ?? 0,
      copiesAssociees: statsCopies?.associees ?? 0,
      copiesNonAssociees: statsCopies?.non_associees ?? 0,
      associationsAujourdhui,
      associationsSemaine,
      parMatiere: Object.entries(parMatiere).map(([name, value]) => ({ name, value })),
      parJour: Object.entries(parJour).map(([date, value]) => ({
        date: new Date(date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' }),
        value
      })),
      parMode: Object.entries(parMode).map(([name, value]) => ({ name, value })),
      tauxAssociation: statsCopies && statsCopies.total > 0
        ? Math.round((statsCopies.associees / statsCopies.total) * 100)
        : 0
    };
  }, [historiqueArray, copiesAssocieesArray, statsCopies]);

  // Filtrer l'historique
  // Filtrage serveur — les données sont déjà filtrées et paginées
  const historiqueFiltree = historiqueArray;

  // Export Excel
  const exportToExcel = (data: any[], filename: string) => {
    // Créer le contenu CSV
    const headers = Object.keys(data[0] || {});
    const csvContent = [
      headers.join(';'),
      ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(';'))
    ].join('\n');

    // Ajouter BOM pour Excel
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleExportCopiesAssociees = () => {
    const data = copiesAssocieesArray.map((c: any) => ({
      'Code Copie': c.code_copie,
      'Matricule Étudiant': c.etudiant_matricule || '',
      'Nom Étudiant': c.etudiant_nom || '',
      'Matière': c.matiere_nom || '',
      'Code Matière': c.matiere_code || '',
      'Lot': c.lot_code || '',
      'Mode Association': c.mode_association || '',
      'Date Association': c.date_association ? new Date(c.date_association).toLocaleString('fr-FR') : '',
      'Statut': c.est_associee ? 'Associée' : 'Non associée'
    }));
    exportToExcel(data, 'copies_associees');
    setMessage({ type: 'success', text: 'Export Excel généré avec succès !' });
  };

  const handleExportHistorique = () => {
    const data = historiqueFiltree.map(h => ({
      'Code Copie': h.copie_code,
      'Action': h.action,
      'Ancien Matricule': h.ancien_etudiant_matricule || '',
      'Nouveau Matricule': h.nouvel_etudiant_matricule || '',
      'Effectué par': h.effectue_par_nom,
      'Date': new Date(h.date_action).toLocaleString('fr-FR'),
      'Commentaire': h.commentaire || '',
      'Adresse IP': h.adresse_ip || ''
    }));
    exportToExcel(data, 'historique_associations');
    setMessage({ type: 'success', text: 'Export historique généré avec succès !' });
  };

  // Impression PDF
  const handlePrint = (type: 'associees' | 'historique') => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    let content = '';
    const date = new Date().toLocaleDateString('fr-FR');
    const heure = new Date().toLocaleTimeString('fr-FR');

    if (type === 'associees') {
      content = `
        <html>
          <head>
            <title>Liste des copies associées</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              h1 { color: #1e40af; text-align: center; }
              .header { text-align: center; margin-bottom: 20px; color: #666; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #1e40af; color: white; }
              tr:nth-child(even) { background-color: #f9fafb; }
              .footer { margin-top: 20px; text-align: center; color: #666; font-size: 12px; }
              .stats { display: flex; justify-content: space-around; margin-bottom: 20px; }
              .stat-box { padding: 10px 20px; background: #f3f4f6; border-radius: 8px; text-align: center; }
              .stat-value { font-size: 24px; font-weight: bold; color: #1e40af; }
              @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
            </style>
          </head>
          <body>
            <h1>Liste des Copies Associées</h1>
            <div class="header">
              <p>Généré le ${date} à ${heure}</p>
            </div>
            <div class="stats">
              <div class="stat-box">
                <div class="stat-value">${copiesAssocieesArray.length}</div>
                <div>Copies associées</div>
              </div>
              <div class="stat-box">
                <div class="stat-value">${statsData.tauxAssociation}%</div>
                <div>Taux d'association</div>
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Code Copie</th>
                  <th>Matricule</th>
                  <th>Nom Étudiant</th>
                  <th>Matière</th>
                  <th>Date Association</th>
                </tr>
              </thead>
              <tbody>
                ${copiesAssocieesArray.map((c: any) => `
                  <tr>
                    <td>${c.code_copie}</td>
                    <td>${c.etudiant_matricule || ''}</td>
                    <td>${c.etudiant_nom || ''}</td>
                    <td>${c.matiere_nom || ''}</td>
                    <td>${c.date_association ? new Date(c.date_association).toLocaleString('fr-FR') : ''}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            <div class="footer">
              <p>Document généré automatiquement - Système de Gestion des Notes</p>
            </div>
          </body>
        </html>
      `;
    } else {
      content = `
        <html>
          <head>
            <title>Historique des associations</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              h1 { color: #1e40af; text-align: center; }
              .header { text-align: center; margin-bottom: 20px; color: #666; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
              th { background-color: #1e40af; color: white; }
              tr:nth-child(even) { background-color: #f9fafb; }
              .footer { margin-top: 20px; text-align: center; color: #666; font-size: 12px; }
              .action-ASSOCIATION { color: #059669; }
              .action-MODIFICATION { color: #d97706; }
              .action-SUPPRESSION { color: #dc2626; }
              @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
            </style>
          </head>
          <body>
            <h1>Historique des Associations</h1>
            <div class="header">
              <p>Généré le ${date} à ${heure}</p>
              <p>Total: ${historiqueFiltree.length} entrées</p>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Date/Heure</th>
                  <th>Code Copie</th>
                  <th>Action</th>
                  <th>Matricule</th>
                  <th>Effectué par</th>
                  <th>Commentaire</th>
                </tr>
              </thead>
              <tbody>
                ${historiqueFiltree.map(h => `
                  <tr>
                    <td>${new Date(h.date_action).toLocaleString('fr-FR')}</td>
                    <td>${h.copie_code}</td>
                    <td class="action-${h.action}">${h.action}</td>
                    <td>${h.nouvel_etudiant_matricule || h.ancien_etudiant_matricule || ''}</td>
                    <td>${h.effectue_par_nom}</td>
                    <td>${h.commentaire || ''}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            <div class="footer">
              <p>Document généré automatiquement - Système de Gestion des Notes</p>
            </div>
          </body>
        </html>
      `;
    }

    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  // Indicateur global de chargement (true si une des queries principales est en cours)
  const isAnyLoading = fetchingEtudiants || fetchingMatieres || fetchingHistorique || fetchingAssociees || fetchingSansFormateur;

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 overflow-x-hidden relative">
      {/* Barre de progression globale en haut de la page */}
      {isAnyLoading && (
        <div className="fixed top-0 left-0 right-0 z-[100] h-1 bg-transparent overflow-hidden">
          <div className="h-full bg-gradient-to-r from-blue-500 via-purple-500 via-pink-500 to-blue-500 bg-[length:200%_100%] animate-progress-slide shadow-lg shadow-blue-500/50" />
        </div>
      )}

      {/* En-tête compact — laisse la place au scan/association */}
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
            <UserCheck className="shrink-0" size={16} />
            Association Copies
            {sessionMode === 'RATTRAPAGE' && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 border border-orange-300 dark:border-orange-700">
                ↻ Rattrapage
              </span>
            )}
          </h1>

          {/* Toggles : mode session + parité semestre — tailles compactes
              pour tenir dans le cadre. Verrouillés si config admin active. */}
          <div className="mt-1 flex flex-wrap items-center gap-1">
            <div
              className={`inline-flex items-center rounded-md p-0.5 border ${
                verrouSession
                  ? 'bg-red-50 dark:bg-red-950/30 border-red-300 dark:border-red-700'
                  : 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
              }`}
              title={verrouSession ? 'Verrouillé par l\'administrateur — modifier dans Paramètres → Verrouillage Association des copies' : undefined}
            >
              {verrouSession && <Lock size={10} className="text-red-500 ml-1 shrink-0" />}
              <button
                onClick={() => !verrouSession && setSessionMode('NORMAL')}
                disabled={!!verrouSession && verrouSession !== 'NORMAL'}
                className={`px-2 py-1 text-[11px] font-medium rounded transition-colors ${
                  sessionMode === 'NORMAL'
                    ? 'bg-blue-600 text-white shadow'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                } ${verrouSession && verrouSession !== 'NORMAL' ? 'opacity-40 cursor-not-allowed' : ''}`}
              >
                Ordinaire
              </button>
              <button
                onClick={() => !verrouSession && setSessionMode('RATTRAPAGE')}
                disabled={!!verrouSession && verrouSession !== 'RATTRAPAGE'}
                className={`px-2 py-1 text-[11px] font-medium rounded transition-colors ${
                  sessionMode === 'RATTRAPAGE'
                    ? 'bg-orange-600 text-white shadow'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                } ${verrouSession && verrouSession !== 'RATTRAPAGE' ? 'opacity-40 cursor-not-allowed' : ''}`}
              >
                ↻ Rattrapage
              </button>
            </div>

            {/* Parité du semestre : limite les matières affichées dans le picker */}
            <div
              className={`inline-flex items-center rounded-md p-0.5 border ${
                verrouParity
                  ? 'bg-red-50 dark:bg-red-950/30 border-red-300 dark:border-red-700'
                  : 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
              }`}
              title={verrouParity ? 'Verrouillé par l\'administrateur — modifier dans Paramètres → Verrouillage Association des copies' : undefined}
            >
              {verrouParity && <Lock size={10} className="text-red-500 ml-1 shrink-0" />}
              <button
                onClick={() => !verrouParity && setSemestreParity('impair')}
                disabled={!!verrouParity && verrouParity !== 'impair'}
                className={`px-2 py-1 text-[11px] font-medium rounded transition-colors ${
                  semestreParity === 'impair'
                    ? 'bg-emerald-600 text-white shadow'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                } ${verrouParity && verrouParity !== 'impair' ? 'opacity-40 cursor-not-allowed' : ''}`}
                title="Semestres impairs (S1/S3/S5)"
              >
                Impairs
              </button>
              <button
                onClick={() => !verrouParity && setSemestreParity('pair')}
                disabled={!!verrouParity && verrouParity !== 'pair'}
                className={`px-2 py-1 text-[11px] font-medium rounded transition-colors ${
                  semestreParity === 'pair'
                    ? 'bg-purple-600 text-white shadow'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                } ${verrouParity && verrouParity !== 'pair' ? 'opacity-40 cursor-not-allowed' : ''}`}
                title="Semestres pairs (S2/S4/S6)"
              >
                Pairs
              </button>
            </div>
            {(verrouSession || verrouParity) && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded border border-red-200 dark:border-red-800">
                <Lock size={9} /> Verrouillé par admin
              </span>
            )}
          </div>
        </div>

        {/* Boutons d'actions — compacts pour maximiser la surface du cadre scan */}
        <div className="flex flex-wrap gap-1">
          <button
            onClick={() => setShowProgressionEDT(true)}
            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-[11px] font-medium"
            title="Progression association vs EDT (attendus/associés/écart par matière, jour, créneau)"
          >
            <TrendingUp size={12} /> Progression
          </button>
          <button
            onClick={() => setShowAnalyse(true)}
            className="inline-flex items-center gap-1 px-2 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 text-[11px] font-medium"
            title="Analyses"
          >
            <BarChart3 size={12} /> Analyses
          </button>
          <button
            onClick={() => setShowListeAssociees(true)}
            className="inline-flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-[11px] font-medium"
            title="Copies associées"
          >
            <Users size={12} /> Associées
          </button>
          <button
            onClick={() => setShowHistorique(true)}
            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-[11px] font-medium"
            title="Historique des associations"
          >
            <History size={12} /> Historique
          </button>
          <button
            onClick={() => setShowAttribFormateur(true)}
            className="inline-flex items-center gap-1 px-2 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 text-[11px] font-medium"
            title="Attributions formateurs"
          >
            <UserCheck size={12} /> Formateurs
          </button>
          <button
            onClick={() => setShowSansFormateur(true)}
            className="inline-flex items-center gap-1 px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-[11px] font-medium"
            title="Matières sans formateur"
          >
            <AlertTriangle size={12} /> Sans form.
          </button>
          <button
            onClick={() => setShowDissocierLot(true)}
            className="inline-flex items-center gap-1 px-2 py-1 bg-rose-700 text-white rounded hover:bg-rose-800 text-[11px] font-medium"
            title="Dissocier en lot par matière (avec filtre formateur, sélection à la carte)"
          >
            <Unlink size={12} /> Dissocier lot
          </button>
          <button
            onClick={() => setShowTransfererMatiere(true)}
            className="inline-flex items-center gap-1 px-2 py-1 bg-rose-600 text-white rounded hover:bg-rose-700 text-[11px] font-medium"
            title="Transférer toutes les copies d'une matière vers une autre (ex : Électrotechnique → Électronique II)"
          >
            ↔ Transférer matière
          </button>
          <button
            onClick={() => {
              refetchStats();
              refetchAssociees();
              refetchHistorique();
              refetchEtudiants();
              refetchMatieres();
            }}
            className="inline-flex items-center gap-1 px-2 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 text-[11px] font-medium"
            title="Rafraîchir les données"
          >
            <RefreshCw size={12} /> Actualiser
          </button>
        </div>
      </div>

      {/* Message de feedback */}
      {message && (
        <div
          className={`mb-6 p-4 rounded-lg flex items-center ${
            message.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-700'
              : 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-700'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle2 className="mr-2" size={20} />
          ) : (
            <XCircle className="mr-2" size={20} />
          )}
          {message.text}
          <button
            onClick={() => setMessage(null)}
            className="ml-auto text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Alerte copies orphelines */}
      {statsCopies && statsCopies.orphelines > 0 && (
        <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-800 dark:text-amber-200">
                {statsCopies.orphelines.toLocaleString('fr-FR')} copies orphelines détectées
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                Ces copies ne correspondent à aucun lot de copies visible. Elles proviennent probablement d'anciennes générations.
                Seules les copies des lots imprimés doivent être utilisées pour l'association.
              </p>
              {orphelinesData && (
                <div className="mt-2 text-sm text-amber-700 dark:text-amber-300 grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <div>Orphelines totales : <strong>{orphelinesData.orphelines.toLocaleString('fr-FR')}</strong></div>
                  <div>Déjà associées : <strong>{orphelinesData.orphelines_associees}</strong></div>
                  <div>Copies valides : <strong>{orphelinesData.copies_valides.toLocaleString('fr-FR')}</strong></div>
                </div>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              {!orphelinesData && (
                <button
                  onClick={() => compterOrphelines()}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-md hover:bg-amber-700 text-sm font-medium"
                >
                  <Info size={14} />
                  Détails
                </button>
              )}
              <button
                onClick={async () => {
                  if (!orphelinesData) {
                    await compterOrphelines();
                  }
                  const detail = orphelinesData;
                  const assocCount = detail?.orphelines_associees ?? 0;
                  const total = detail?.orphelines ?? statsCopies.orphelines;
                  const msg = assocCount > 0
                    ? `Supprimer ${total.toLocaleString('fr-FR')} copies orphelines dont ${assocCount} déjà associées ?\n\nCette action est irréversible.`
                    : `Supprimer ${total.toLocaleString('fr-FR')} copies orphelines ?\n\nCette action est irréversible.`;
                  if (!window.confirm(msg)) return;
                  try {
                    const result = await purgerOrphelines({ forcer: true }).unwrap();
                    setMessage({ type: 'success', text: result.message });
                    refetchStats();
                    refetchAssociees();
                  } catch (err: any) {
                    setMessage({
                      type: 'error',
                      text: err?.data?.error || 'Erreur lors de la purge',
                    });
                  }
                }}
                disabled={purgeLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium disabled:opacity-50"
              >
                {purgeLoading ? <RefreshCw size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Purger
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Zone de saisie */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/50 p-4 sm:p-6">
            {/* Sélection du mode */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mode d'association
              </label>
              <div className="flex space-x-4">
                <button
                  onClick={() => setModeAssociation('SCAN')}
                  className={`flex-1 py-3 px-4 rounded-md flex items-center justify-center ${
                    modeAssociation === 'SCAN'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <Scan className="mr-2" size={20} />
                  Scan Code-Barres
                </button>
                <button
                  onClick={() => setModeAssociation('MANUEL')}
                  className={`flex-1 py-3 px-4 rounded-md flex items-center justify-center ${
                    modeAssociation === 'MANUEL'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <Search className="mr-2" size={20} />
                  Saisie Manuelle
                </button>
              </div>
            </div>

            {/* ① MATIÈRE + FORMATEUR — compacts sur une grille */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                  <BookOpen size={14} />
                  Matière <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setMatiereVerrouilee((v) => !v)}
                  title={matiereVerrouilee ? 'Déverrouiller la matière' : 'Verrouiller la matière (même matière pour tout le lot)'}
                  className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border transition-colors ${
                    matiereVerrouilee
                      ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-700 hover:bg-amber-200 dark:hover:bg-amber-900/50'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {matiereVerrouilee ? (
                    <><Lock size={13} /> Verrouillée</>
                  ) : (
                    <><Unlock size={13} /> Verrouiller</>
                  )}
                </button>
              </div>

              {matiereVerrouilee && selectedMatiereGroupe ? (
                /* Matière verrouillée : affichage en lecture seule */
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-md text-sm">
                  <Lock size={16} className="text-amber-600 dark:text-amber-400 shrink-0" />
                  <div className="flex-1 flex items-center gap-2 flex-wrap">
                    <span className="text-amber-900 dark:text-amber-200 font-medium">{selectedMatiereGroupe.nom_matiere}</span>
                    {classesFormateurAffichage.map(c => (
                      <span key={c} className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded text-xs font-semibold">
                        {c}
                      </span>
                    ))}
                  </div>
                  <span className="text-xs text-amber-600 dark:text-amber-400 italic">Verrouillée</span>
                </div>
              ) : (
                <AutocompleteMatiere
                  matieres={matieresArray}
                  value={selectedMatiere}
                  onChange={setSelectedMatiere}
                  onSelect={handleSelectMatiereGroupe}
                  disabled={false}
                  selectedNom={selectedMatiereGroupe ? `${selectedMatiereGroupe.nom_matiere}  [${classesFormateurAffichage.join(', ')}]` : ''}
                />
              )}

            </div>

            {/* ①bis FORMATEUR — compact */}
            {selectedMatiereGroupe && (
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Formateur {selectedEnseignantId && <span className="text-green-600 dark:text-green-400">(pré-sélectionné)</span>}
                </label>
                <select
                  value={selectedEnseignantId ?? ''}
                  onChange={(e) => setSelectedEnseignantId(e.target.value ? Number(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="">— Formateur —</option>
                  {formateurs.map((f: any) => {
                    const isAttribue = attributionsAnnee && selectedMatiereGroupe.matieres.some(
                      (m: any) => attributionsAnnee[String(m.id)]?.formateur_id === f.id
                    );
                    return (
                      <option key={f.id} value={f.id}>
                        {f.nom} {f.prenoms}{isAttribue ? ' ★' : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}

            {/* ② CODE COPIE — compact */}
            <div className="mb-2">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Code copie <span className="text-red-500">*</span>
                {modeAssociation === 'SCAN' && <span className="ml-1 text-[10px] font-normal text-blue-600">(auto-focus)</span>}
              </label>
              <div className="flex w-full">
                <div className="relative flex-1 min-w-0">
                  <input
                    ref={inputRef}
                    type="text"
                    value={codeCopie}
                    onChange={(e) => setCodeCopie(e.target.value.toUpperCase())}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setCodeCopie('');
                        setCopieInfo(null);
                        inputRef.current?.focus();
                        return;
                      }
                      handleKeyPress(e, 'copie');
                    }}
                    placeholder={
                      modeAssociation === 'SCAN'
                        ? 'Scannez le code-barres de la copie...'
                        : 'Saisissez le code de la copie (Échap pour effacer)'
                    }
                    className="w-full px-3 py-2 pr-9 border border-gray-300 dark:border-gray-600 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 text-base font-mono bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                  />
                  {codeCopie && (
                    <button
                      type="button"
                      onClick={() => { setCodeCopie(''); setCopieInfo(null); inputRef.current?.focus(); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition"
                      title="Effacer (Échap)"
                    >
                      <XCircle size={20} />
                    </button>
                  )}
                </div>
                <button
                  onClick={handleRechercherCopie}
                  disabled={rechercheLoading || !codeCopie}
                  className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {rechercheLoading ? (
                    <RefreshCw size={20} className="animate-spin" />
                  ) : (
                    <Search size={20} />
                  )}
                </button>
                {/* Bouton scanner caméra (mobile) */}
                <button
                  type="button"
                  onClick={() => setShowCameraScanner(true)}
                  className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 ml-1 flex items-center gap-1 text-sm"
                  title="Scanner avec la caméra"
                >
                  <Camera size={18} />
                  <span className="hidden sm:inline">Scanner</span>
                </button>
              </div>
            </div>

            {/* Informations de la copie trouvée */}
            {copieInfo && (
              <div className={`mb-2 p-2.5 border rounded-md text-xs ${copieInfo.est_associee ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700' : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'}`}>
                <div className="flex justify-between items-center mb-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-semibold text-sm ${copieInfo.est_associee ? 'text-green-900 dark:text-green-200' : 'text-blue-900 dark:text-blue-200'}`}>
                      {copieInfo.code_copie} — {copieInfo.est_associee ? 'Associée' : 'Non associée'}
                    </span>
                    {/* Badge type d'examen */}
                    {(copieInfo as any).type_examen === 'RATTRAPAGE' && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 border border-orange-300 dark:border-orange-700">
                        ↻ Rattrapage
                      </span>
                    )}
                    {(copieInfo as any).type_examen === 'NORMAL' && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                        Session ordinaire
                      </span>
                    )}
                  </div>
                  {copieInfo.est_associee && (
                    <button
                      onClick={() => handleDissocier(copieInfo.code_copie)}
                      className="flex items-center px-3 py-1 bg-red-600 text-white text-sm rounded-md hover:bg-red-700"
                    >
                      <Unlink size={14} className="mr-1" />
                      Dissocier
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className={copieInfo.est_associee ? 'text-green-700 dark:text-green-300' : 'text-blue-700 dark:text-blue-300'}>Code:</span>{' '}
                    <span className="font-mono font-semibold text-gray-900 dark:text-gray-100">{copieInfo.code_copie}</span>
                  </div>
                  <div>
                    <span className={copieInfo.est_associee ? 'text-green-700 dark:text-green-300' : 'text-blue-700 dark:text-blue-300'}>Matière (lot):</span>{' '}
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{copieInfo.matiere_nom || 'Non définie'}</span>
                  </div>
                  <div>
                    <span className={copieInfo.est_associee ? 'text-green-700 dark:text-green-300' : 'text-blue-700 dark:text-blue-300'}>Lot:</span> <span className="text-gray-900 dark:text-gray-100">{copieInfo.lot_code || 'N/A'}</span>
                  </div>
                  <div>
                    <span className={copieInfo.est_associee ? 'text-green-700 dark:text-green-300' : 'text-blue-700 dark:text-blue-300'}>Statut:</span>{' '}
                    <span className={`font-semibold ${copieInfo.est_associee ? 'text-green-700 dark:text-green-300' : 'text-orange-700 dark:text-orange-400'}`}>
                      {copieInfo.est_associee ? 'Associée' : 'Non associée'}
                    </span>
                  </div>
                  {copieInfo.est_associee && (
                    <>
                      <div>
                        <span className="text-green-700 dark:text-green-300">Étudiant:</span>{' '}
                        <span className="font-semibold text-gray-900 dark:text-gray-100">{copieInfo.etudiant_matricule}</span>
                      </div>
                      <div>
                        <span className="text-green-700 dark:text-green-300">Nom:</span>{' '}
                        <span className="font-semibold text-gray-900 dark:text-gray-100">{copieInfo.etudiant_nom}</span>
                      </div>
                      <div>
                        <span className="text-green-700 dark:text-green-300">Classe:</span>{' '}
                        <span className="font-semibold text-gray-900 dark:text-gray-100">{copieInfo.etudiant_classe || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-green-700 dark:text-green-300">Niveau:</span>{' '}
                        <span className="font-semibold text-gray-900 dark:text-gray-100">{copieInfo.etudiant_niveau || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-green-700 dark:text-green-300">Option:</span>{' '}
                        <span className="font-semibold text-gray-900 dark:text-gray-100">{copieInfo.etudiant_option || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-green-700 dark:text-green-300">Formateur:</span>{' '}
                        <span className="font-semibold text-gray-900 dark:text-gray-100">{copieInfo.enseignant_nom || 'Non attribué'}</span>
                      </div>
                      <div>
                        <span className="text-green-700 dark:text-green-300">Associée par:</span>{' '}
                        <span className="font-semibold text-gray-900 dark:text-gray-100">{copieInfo.associee_par_nom || 'N/A'}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* ③ ÉTUDIANT — compact */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                <Users size={14} />
                Étudiant <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2 w-full">
                <div className="flex-1 min-w-0">
                  <AutocompleteEtudiant
                    etudiants={etudiantsArray}
                    value={matriculeEtudiant}
                    onChange={setMatriculeEtudiant}
                    onSelect={handleSelectEtudiant}
                    disabled={!copieInfo || copieInfo?.est_associee}
                    placeholder="Nom ou matricule..."
                    matiereOption={selectedMatiereGroupe?.matieres.length === 1 ? selectedMatiereGroupe.matieres[0].option : undefined}
                    inputRef={etudiantInputRef}
                    onEnter={handleAssocier}
                  />
                </div>
                <button
                  onClick={handleAssocier}
                  disabled={
                    associationLoading ||
                    !codeCopie ||
                    !matriculeEtudiant ||
                    !selectedMatiereGroupe ||
                    copieInfo?.est_associee
                  }
                  className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center shrink-0 text-sm"
                >
                  {associationLoading ? (
                    <RefreshCw size={20} className="animate-spin" />
                  ) : (
                    <>
                      <UserCheck size={20} className="mr-2" />
                      Associer
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Warning incompatibilité option étudiant / matière (informatif, ne bloque pas) */}
            {optionMismatch && etudiantInfo && selectedMatiereGroupe && (
              <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-md flex items-start gap-3">
                <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800 dark:text-amber-200">
                  <p className="font-medium">
                    Attention : {etudiantInfo.nom} est en « {etudiantInfo.option || etudiantInfo.classe} ».
                    L'association reste possible.
                  </p>
                </div>
              </div>
            )}

            {/* Informations de l'étudiant sélectionné */}
            {etudiantInfo && !optionMismatch && (
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-md">
                <h3 className="font-semibold text-green-900 dark:text-green-200 mb-2">Informations de l'étudiant</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-green-700 dark:text-green-300">Matricule:</span>{' '}
                    <span className="font-mono font-semibold text-gray-900 dark:text-gray-100">{etudiantInfo.matricule}</span>
                  </div>
                  <div>
                    <span className="text-green-700 dark:text-green-300">Nom:</span>{' '}
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{etudiantInfo.nom} {etudiantInfo.prenoms || etudiantInfo.prenom}</span>
                  </div>
                  <div>
                    <span className="text-green-700 dark:text-green-300">Classe:</span>{' '}
                    <span className="font-semibold text-blue-600 dark:text-blue-400">{etudiantInfo.classe || `${etudiantInfo.niveau}${etudiantInfo.option || ''}`}</span>
                  </div>
                  <div>
                    <span className="text-green-700 dark:text-green-300">Option:</span> <span className="text-gray-900 dark:text-gray-100">{etudiantInfo.option || 'N/A'}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Statistiques */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/50 p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Copies non associées</div>
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400 mt-1">
                {statsData.copiesNonAssociees}
              </div>
              <RepartitionMini data={(statsCopies as any)?.repartition?.non_associees} />
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/50 p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Copies associées</div>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                {statsData.copiesAssociees}
              </div>
              <RepartitionMini data={(statsCopies as any)?.repartition?.associees} />
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/50 p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Aujourd'hui</div>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
                {statsData.associationsAujourdhui}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/50 p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Taux d'association</div>
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">
                {statsData.tauxAssociation}%
              </div>
            </div>
          </div>
        </div>

        {/* Panneau latéral */}
        <div className="lg:col-span-1">
          {/* Instructions */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-4 flex items-center">
              {modeAssociation === 'SCAN' ? (
                <><Scan className="mr-2" size={20} /> Mode Scan</>
              ) : (
                <><Search className="mr-2" size={20} /> Mode Manuel</>
              )}
            </h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800 dark:text-blue-300">
              {modeAssociation === 'SCAN' ? (
                <>
                  <li>Sélectionnez la <strong>matière</strong> en premier</li>
                  <li>Verrouillez-la si tout le lot est de la même matière (<Lock size={11} className="inline" />)</li>
                  <li>Connectez le lecteur de code-barres USB</li>
                  <li>Scannez la copie → le focus saute vers Étudiant</li>
                  <li>Scannez ou tapez le matricule/nom de l'étudiant</li>
                  <li>Appuyez sur <kbd className="px-1 bg-white dark:bg-gray-700 border border-blue-300 dark:border-blue-600 rounded text-xs text-blue-800 dark:text-blue-300">Entrée</kbd> pour valider</li>
                </>
              ) : (
                <>
                  <li>Sélectionnez la <strong>matière</strong> (verrouillez si besoin)</li>
                  <li>Saisissez le code copie et cliquez sur Rechercher</li>
                  <li>Recherchez l'étudiant par matricule ou nom</li>
                  <li>Cliquez sur "Associer" pour valider</li>
                </>
              )}
            </ol>
          </div>

          {/* Dernières associations */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
              <History className="mr-2" size={20} />
              Dernières associations
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {historiqueArray.slice(0, 10).map((h) => (
                <div
                  key={h.id}
                  className="text-xs p-2 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600"
                >
                  <div className="flex justify-between items-start">
                    <div className="font-mono font-semibold text-gray-900 dark:text-gray-100">{h.copie_code}</div>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                      h.action === 'ASSOCIATION' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' :
                      h.action === 'MODIFICATION' ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300' :
                      'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                    }`}>
                      {h.action}
                    </span>
                  </div>
                  <div className="text-gray-600 dark:text-gray-400">
                    → {h.nouvel_etudiant_matricule || h.ancien_etudiant_matricule}
                  </div>
                  <div className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                    {new Date(h.date_action).toLocaleString('fr-FR')}
                  </div>
                </div>
              ))}
              {historiqueArray.length === 0 && (
                <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                  Aucune association pour le moment
                </div>
              )}
            </div>
            {historiqueArray.length > 0 && (
              <button
                onClick={() => setShowHistorique(true)}
                className="mt-4 w-full py-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium"
              >
                Voir tout l'historique →
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Modal Analyse */}
      <Modal
        isOpen={showAnalyse}
        onClose={() => setShowAnalyse(false)}
        title="Analyse des Associations"
        size="xl"
      >
        <div className="space-y-6">
          {/* Stats summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{statsData.totalCopies}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total copies</div>
              <RepartitionMini data={(statsCopies as any)?.repartition?.total} />
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-green-600 dark:text-green-400">{statsData.copiesAssociees}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Associées</div>
              <RepartitionMini data={(statsCopies as any)?.repartition?.associees} />
            </div>
            <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">{statsData.copiesNonAssociees}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Non associées</div>
              <RepartitionMini data={(statsCopies as any)?.repartition?.non_associees} />
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">{statsData.tauxAssociation}%</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Taux global</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Tendance sur 7 jours */}
            <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center">
                <TrendingUp size={18} className="mr-2 text-blue-600 dark:text-blue-400" />
                Tendance (7 derniers jours)
              </h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={statsData.parJour}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} name="Associations" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Par mode */}
            <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center">
                <PieChart size={18} className="mr-2 text-green-600 dark:text-green-400" />
                Répartition par mode
              </h4>
              <ResponsiveContainer width="100%" height={200}>
                <RechartsPieChart>
                  <Pie
                    data={statsData.parMode}
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                  >
                    {statsData.parMode.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Par matière */}
          <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center">
              <BookOpen size={18} className="mr-2 text-purple-600 dark:text-purple-400" />
              Associations par matière
            </h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={statsData.parMatiere.slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" fontSize={12} />
                <YAxis dataKey="name" type="category" width={150} fontSize={11} />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" fill="#8b5cf6" name="Copies associées" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Statistiques temporelles */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <div className="flex items-center text-gray-600 dark:text-gray-400 mb-2">
                <Clock size={16} className="mr-2" />
                Aujourd'hui
              </div>
              <div className="text-2xl font-bold text-gray-800 dark:text-gray-200">{statsData.associationsAujourdhui}</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <div className="flex items-center text-gray-600 dark:text-gray-400 mb-2">
                <Calendar size={16} className="mr-2" />
                Cette semaine
              </div>
              <div className="text-2xl font-bold text-gray-800 dark:text-gray-200">{statsData.associationsSemaine}</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <div className="flex items-center text-gray-600 dark:text-gray-400 mb-2">
                <History size={16} className="mr-2" />
                Total historique
              </div>
              <div className="text-2xl font-bold text-gray-800 dark:text-gray-200">{historiqueArray.length}</div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Modal Liste des copies associées */}
      <Modal
        isOpen={showListeAssociees}
        onClose={() => setShowListeAssociees(false)}
        title="Copies Associées"
        size="xl"
      >
        <div className="space-y-4">
          {/* Actions */}
          <div className="flex flex-wrap gap-2 pb-4 border-b dark:border-gray-700">
            <button
              onClick={() => handlePrint('associees')}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Printer size={16} className="mr-2" />
              Imprimer PDF
            </button>
            <button
              onClick={handleExportCopiesAssociees}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              <FileSpreadsheet size={16} className="mr-2" />
              Exporter Excel
            </button>
          </div>

          {/* Tableau */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Code Copie</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Matricule</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Nom Étudiant</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Classe</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Matière</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {copiesAssocieesArray.map((copie: any) => (
                  <tr key={copie.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-gray-100">{copie.code_copie}</td>
                    <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-gray-100">{copie.etudiant_matricule}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{copie.etudiant_nom}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 rounded text-xs font-semibold">
                        {copie.etudiant_classe || copie.etudiant_option || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{copie.matiere_nom}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {copie.date_association ? new Date(copie.date_association).toLocaleString('fr-FR') : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <button
                        onClick={() => handleDissocier(copie.code_copie)}
                        className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 flex items-center"
                        title="Dissocier"
                      >
                        <Unlink size={16} className="mr-1" />
                        Dissocier
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {copiesAssocieesArray.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Aucune copie associée pour le moment
              </div>
            )}
          </div>

          <div className="text-sm text-gray-500 dark:text-gray-400 text-right">
            Total: {copiesAssocieesArray.length} copies associées
          </div>
        </div>
      </Modal>

      {/* Modal Historique détaillé */}
      <Modal
        isOpen={showHistorique}
        onClose={() => { setShowHistorique(false); setHistPage(1); setHistSearch(''); setHistAction(''); }}
        title="Historique Détaillé des Associations"
        size="xl"
      >
        <div className="space-y-4">
          {/* Barre de progression animée */}
          {fetchingHistorique && (
            <div className="relative w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 rounded-full animate-loading-bar" />
            </div>
          )}

          {/* Filtres */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <Filter size={18} className="mr-2 text-gray-600 dark:text-gray-400" />
                <span className="font-medium text-gray-700 dark:text-gray-300">Filtres</span>
                {(histMatiere || histUtilisateur || histClasse || histFormateur) && (
                  <span className="ml-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full text-[10px] font-medium">
                    {[histMatiere && 'Matière', histClasse && 'Classe', histFormateur && 'Formateur', histUtilisateur && 'Utilisateur'].filter(Boolean).join(' + ')} actif
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setHistSearch(''); setHistAction(''); setHistDateDebut(''); setHistDateFin(''); setHistMatiere(''); setHistUtilisateur(''); setHistClasse(''); setHistFormateur(''); setHistPage(1); }}
                  className="flex items-center px-3 py-1.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 text-xs"
                >
                  <RefreshCw size={12} className="mr-1" /> Réinit.
                </button>
                <button
                  onClick={handleExportHistorique}
                  className="flex items-center px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 text-xs"
                >
                  <FileSpreadsheet size={12} className="mr-1" /> Excel
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Date début</label>
                <input type="date" value={histDateDebut}
                  onChange={(e) => { setHistDateDebut(e.target.value); setHistPage(1); }}
                  className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Date fin</label>
                <input type="date" value={histDateFin}
                  onChange={(e) => { setHistDateFin(e.target.value); setHistPage(1); }}
                  className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Action</label>
                <select value={histAction}
                  onChange={(e) => { setHistAction(e.target.value); setHistPage(1); }}
                  className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                  <option value="">Toutes</option>
                  <option value="ASSOCIATION">Association</option>
                  <option value="MODIFICATION">Modification</option>
                  <option value="SUPPRESSION">Suppression</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Matière
                  {histMatiere && <span className="ml-1 text-[9px] text-blue-600 dark:text-blue-400">●</span>}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={histMatiere}
                    onChange={(e) => { setHistMatiere(e.target.value); setHistPage(1); }}
                    placeholder="Rechercher matière..."
                    list="hist-matieres-list"
                    className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  />
                  <datalist id="hist-matieres-list">
                    {historiqueStats?.matieres_uniques.map(m => <option key={m} value={m} />)}
                  </datalist>
                  {histMatiere && (
                    <button onClick={() => { setHistMatiere(''); setHistPage(1); }}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Formateur
                  {histFormateur && <span className="ml-1 text-[9px] text-blue-600 dark:text-blue-400">●</span>}
                </label>
                <select value={histFormateur}
                  onChange={(e) => { setHistFormateur(e.target.value); setHistPage(1); }}
                  className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                  <option value="">Tous les formateurs</option>
                  {historiqueStats?.formateurs_uniques?.map((f: string) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Utilisateur
                  {histUtilisateur && <span className="ml-1 text-[9px] text-blue-600 dark:text-blue-400">●</span>}
                </label>
                <select value={histUtilisateur}
                  onChange={(e) => { setHistUtilisateur(e.target.value); setHistPage(1); }}
                  className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                  <option value="">Tous les utilisateurs</option>
                  {historiqueStats?.utilisateurs_uniques.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Classe
                  {histClasse && <span className="ml-1 text-[9px] text-blue-600 dark:text-blue-400">●</span>}
                </label>
                <select value={histClasse}
                  onChange={(e) => { setHistClasse(e.target.value); setHistPage(1); }}
                  className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                  <option value="">Toutes les classes</option>
                  {historiqueStats?.classes_uniques?.map((c: string) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Recherche</label>
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="text" value={histSearch}
                    onChange={(e) => { setHistSearch(e.target.value); setHistPage(1); }}
                    placeholder="Code, nom, matricule..."
                    className="w-full pl-8 pr-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400" />
                </div>
              </div>
            </div>
            {/* Aide contextuelle quand croisement de filtres */}
            {(histMatiere || histClasse || histUtilisateur || histFormateur) && [histMatiere, histClasse, histUtilisateur, histFormateur].filter(Boolean).length >= 2 && (
              <div className="mt-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-[11px] text-blue-700 dark:text-blue-300">
                Filtrage croisé : {[histMatiere && `matière "${histMatiere}"`, histFormateur && `formateur "${histFormateur}"`, histClasse && `classe "${histClasse}"`, histUtilisateur && `par ${histUtilisateur}`].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>

          {/* Suivi détaillé par utilisateur — collapsible */}
          {historiqueStats && historiqueStats.stats_utilisateurs.length > 0 && (
            <details className="group bg-white dark:bg-gray-800 rounded-xl border border-blue-200 dark:border-blue-800/50 overflow-hidden shadow-sm">
              <summary className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-blue-50/50 dark:hover:bg-blue-900/10 select-none list-none">
                <span className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
                  <span className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-blue-600 dark:text-blue-400 transition-transform group-open:rotate-90">
                    <ChevronRight size={14} />
                  </span>
                  <Users size={16} className="text-blue-500" />
                  Performance par utilisateur
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {historiqueStats.stats_utilisateurs.length} utilisateur(s) — {historiqueStats.total} associations
                  </span>
                  <span className="text-[10px] px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium group-open:hidden">
                    Cliquer pour voir
                  </span>
                </span>
              </summary>
              {(historiqueStats as any).repartition && (
                <div className="px-4 pt-2 pb-1">
                  <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 mb-1">
                    Répartition globale des associations
                  </div>
                  <RepartitionMini data={(historiqueStats as any).repartition} />
                </div>
              )}
              <div className="px-4 pb-4 pt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                {historiqueStats.stats_utilisateurs.map((u, idx) => {
                  const medals = ['#FFD700', '#C0C0C0', '#CD7F32'];
                  return (
                    <div key={u.nom} className={`rounded-lg border p-3 ${
                      idx === 0 ? 'border-yellow-300 dark:border-yellow-700 bg-yellow-50/50 dark:bg-yellow-900/10' :
                      'border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800'
                    }`}>
                      <div className="flex items-center justify-between mb-2.5 pb-2 border-b border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => { setHistUtilisateur(u.nom); setHistPage(1); }}
                          className="flex items-center gap-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 px-1.5 py-0.5 rounded transition-colors"
                          title="Filtrer le tableau par cet utilisateur"
                        >
                          <span className="text-base">{idx < 3 ? ['🥇','🥈','🥉'][idx] : `#${idx+1}`}</span>
                          <span className="font-bold text-sm text-gray-900 dark:text-gray-100">{u.nom}</span>
                          <Filter size={11} className="text-gray-400" />
                        </button>
                        <span className="text-lg font-extrabold text-blue-600 dark:text-blue-400">{u.total} <span className="text-[10px] font-normal text-gray-500">copies</span></span>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">Moyenne :</span>
                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{u.moyenne_par_jour} / jour</span>
                      </div>
                      {(u as any).repartition && (
                        <div className="mb-2">
                          <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 mb-0.5">
                            Répartition :
                          </div>
                          <RepartitionMini data={(u as any).repartition} />
                        </div>
                      )}
                      <div className="mb-2">
                        <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 mb-1">Activité par jour :</div>
                        <div className="flex flex-wrap gap-1">
                          {u.jours.slice(0, 10).map(j => (
                            <span key={j.date} className="px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded text-[9px] font-medium">
                              {new Date(j.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} : <strong>{j.count}</strong>
                            </span>
                          ))}
                          {u.jours.length > 10 && <span className="text-[9px] text-gray-400">+{u.jours.length - 10} jours</span>}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 mb-1">Matières associées (cliquer pour filtrer) :</div>
                        <div className="flex flex-wrap gap-1 max-h-28 overflow-y-auto">
                          {u.matieres.slice(0, 15).map(m => (
                            <button
                              key={m.matiere}
                              onClick={() => { setHistMatiere(m.matiere); setHistUtilisateur(u.nom); setHistPage(1); }}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded text-[9px] border border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors cursor-pointer"
                              title={`Filtrer : ${m.matiere} par ${u.nom}`}
                            >
                              {m.matiere.length > 25 ? m.matiere.slice(0, 23) + '…' : m.matiere}
                              <strong className="bg-green-200 dark:bg-green-800 px-1 rounded text-[8px]">{m.count}</strong>
                            </button>
                          ))}
                          {u.matieres.length > 15 && <span className="text-[9px] text-gray-400">+{u.matieres.length - 15}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </details>
          )}

          {/* Tableau */}
          <div className="overflow-x-auto relative">
            {fetchingHistorique && historiqueArray.length > 0 && (
              <div className="absolute inset-0 bg-white/50 dark:bg-gray-800/50 z-10 flex items-center justify-center">
                <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
                  <RefreshCw size={16} className="animate-spin text-blue-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">Chargement...</span>
                </div>
              </div>
            )}
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Date/Heure</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Code</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Matière</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Formateur</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Étudiant</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Classe</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Action</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Par</th>
                  <th className="px-3 py-2.5 text-center text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Dissocier</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {historiqueFiltree.map((h: any) => (
                  <tr key={h.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {new Date(h.date_action).toLocaleDateString('fr-FR')}<br/>
                      <span className="text-[10px] text-gray-400">{new Date(h.date_action).toLocaleTimeString('fr-FR')}</span>
                    </td>
                    <td className="px-3 py-2 text-xs font-mono text-gray-900 dark:text-gray-100">{h.copie_code}</td>
                    <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300 max-w-[150px] truncate" title={h.matiere_nom || ''}>
                      {h.matiere_nom || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-green-700 dark:text-green-400 max-w-[120px] truncate" title={h.enseignant_nom || ''}>
                      {h.enseignant_nom || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-xs font-medium text-gray-900 dark:text-gray-100">{h.nouvel_etudiant_nom || h.nouvel_etudiant_matricule || '—'}</div>
                      {h.nouvel_etudiant_matricule && h.nouvel_etudiant_nom && (
                        <div className="text-[10px] font-mono text-gray-400">{h.nouvel_etudiant_matricule}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">{h.nouvel_etudiant_classe || '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                        h.action === 'ASSOCIATION' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' :
                        h.action === 'MODIFICATION' ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300' :
                        'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                      }`}>{h.action}</span>
                    </td>
                    <td className="px-3 py-2 text-xs font-medium text-blue-700 dark:text-blue-300">{h.effectue_par_nom || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      {h.action === 'ASSOCIATION' && h.copie_code && (
                        currentUser?.role === 'super_admin' || h.effectue_par_nom === currentUser?.username
                      ) && (
                        <button
                          onClick={() => handleDissocier(h.copie_code)}
                          className="px-2 py-1 text-[10px] font-medium bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-700 rounded hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                          title={`Dissocier ${h.copie_code}`}
                        >
                          Dissocier
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!fetchingHistorique && historiqueFiltree.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Aucun enregistrement trouvé
              </div>
            )}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500 dark:text-gray-400">
              Page {histPage} / {historiqueTotalPages || 1} — {historiqueCount} entrée(s) au total
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setHistPage(1)}
                disabled={histPage <= 1}
                className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                &laquo;
              </button>
              <button
                onClick={() => setHistPage(p => Math.max(1, p - 1))}
                disabled={histPage <= 1}
                className="px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                Précédent
              </button>
              {/* Numéros de page */}
              {Array.from({ length: Math.min(5, historiqueTotalPages) }, (_, i) => {
                let pageNum: number;
                if (historiqueTotalPages <= 5) pageNum = i + 1;
                else if (histPage <= 3) pageNum = i + 1;
                else if (histPage >= historiqueTotalPages - 2) pageNum = historiqueTotalPages - 4 + i;
                else pageNum = histPage - 2 + i;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setHistPage(pageNum)}
                    className={`px-3 py-1 text-xs rounded ${
                      pageNum === histPage
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setHistPage(p => Math.min(historiqueTotalPages, p + 1))}
                disabled={histPage >= historiqueTotalPages}
                className="px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                Suivant
              </button>
              <button
                onClick={() => setHistPage(historiqueTotalPages)}
                disabled={histPage >= historiqueTotalPages}
                className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                &raquo;
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Boîte de dialogue de confirmation d'association */}
      {showConfirmDialog && etudiantInfo && copieInfo && selectedMatiereGroupe && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div
            ref={confirmRef}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (confirmAnimation !== 'success') handleAssocier();
              } else if (e.key === 'Escape') {
                setShowConfirmDialog(false);
                setConfirmAnimation('idle');
                inputRef.current?.focus();
              }
            }}
            className={`bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden outline-none transform transition-all duration-300 ${
              confirmAnimation === 'enter' ? 'animate-[slideUp_0.3s_ease-out]' :
              confirmAnimation === 'success' ? 'animate-[pulse_0.5s_ease-in-out]' : ''
            }`}
            style={{ animationFillMode: 'forwards' }}
          >
            {confirmAnimation === 'success' ? (
              /* ── Animation de succès ── */
              <div className="p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center animate-[bounceIn_0.5s_ease-out]">
                  <CheckCircle2 size={40} className="text-green-500" />
                </div>
                <p className="text-lg font-bold text-green-700 dark:text-green-300">Association réussie !</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{copieInfo?.code_copie} → {etudiantInfo.nom}</p>
              </div>
            ) : (
              /* ── Formulaire de confirmation ── */
              <>
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                  <h3 className="text-white font-bold text-lg">Confirmer l'association</h3>
                  <p className="text-blue-100 text-xs mt-1">Appuyez sur <kbd className="px-1.5 py-0.5 bg-blue-500/50 rounded text-white font-mono text-[10px]">Entrée</kbd> pour valider · <kbd className="px-1.5 py-0.5 bg-blue-500/50 rounded text-white font-mono text-[10px]">Échap</kbd> pour annuler</p>
                </div>
                <div className="p-6 space-y-4">
                  {/* Fix 2026-09-01 : panneau d'erreur affiché DIRECTEMENT
                       dans le dialog (au lieu d'un toast externe). L'opérateur
                       voit immédiatement le problème + peut décider (Annuler
                       / Forcer si applicable). */}
                  {dialogError && (
                    <div className="rounded-xl border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 p-4">
                      <div className="flex items-start gap-2 mb-2">
                        <AlertTriangle size={18} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-red-900 dark:text-red-200 text-sm">
                            {dialogError.type === 'double_composition' ? 'Double composition (fraude)'
                              : dialogError.type === 'copie_deja_associee_autre_etudiant' ? 'Copie déjà attribuée'
                              : dialogError.type === 'formateur_mismatch' ? 'Formateur non déclaré'
                              : dialogError.type === 'matiere_non_programme' ? 'Matière hors programme'
                              : dialogError.type === 'niveau_mismatch' ? 'Niveau incohérent'
                              : 'Association impossible'}
                          </div>
                          <div className="text-xs text-red-800 dark:text-red-300 mt-1 whitespace-pre-line">
                            {dialogError.message}
                          </div>
                        </div>
                      </div>
                      {dialogError.details && (
                        <div className="mt-2 pt-2 border-t border-red-200 dark:border-red-800/50 text-[11px] text-red-900 dark:text-red-200 space-y-0.5">
                          {Object.entries(dialogError.details).map(([k, v]) => {
                            if (v === null || v === undefined || v === '') return null;
                            const label = k.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
                            return (
                              <div key={k}>
                                <span className="opacity-70">{label} :</span>{' '}
                                <span className="font-medium">{String(v)}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Copie */}
                  <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                      <Scan size={20} className="text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider">Copie</p>
                      <p className="font-mono font-bold text-gray-900 dark:text-gray-100">{codeCopie}</p>
                    </div>
                  </div>

                  {/* Flèche */}
                  <div className="flex justify-center">
                    <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center animate-bounce">
                      <span className="text-green-600 dark:text-green-400 text-lg">↓</span>
                    </div>
                  </div>

                  {/* Étudiant */}
                  <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
                    <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center shrink-0">
                      <UserCheck size={20} className="text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider">Étudiant</p>
                      <p className="font-bold text-gray-900 dark:text-gray-100 truncate">{etudiantInfo.nom} {etudiantInfo.prenoms}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">{etudiantInfo.matricule} · {etudiantInfo.classe}</p>
                    </div>
                  </div>

                  {/* Matière */}
                  <div className="flex items-center gap-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center shrink-0">
                      <BookOpen size={20} className="text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider">Matière</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{selectedMatiereGroupe.nom_matiere}</p>
                    </div>
                  </div>

                  {/* Formateur */}
                  {selectedEnseignantId && (
                    <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
                        <Users size={20} className="text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider">Formateur</p>
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {formateurs.find((f: any) => f.id === selectedEnseignantId)?.nom || ''}{' '}
                          {formateurs.find((f: any) => f.id === selectedEnseignantId)?.prenoms || ''}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 px-6 pb-6">
                  <button
                    onClick={() => {
                      setShowConfirmDialog(false); setConfirmAnimation('idle');
                      setDialogError(null); inputRef.current?.focus();
                    }}
                    className="flex-1 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                  >
                    Annuler
                  </button>
                  {/* Fix 2026-09-01 : selon l'état :
                       - Pas d'erreur → bouton "Associer" normal
                       - Erreur avec canForce → 2 boutons (Réessayer, Forcer en rouge)
                       - Erreur bloquante → bouton "Corriger" (ferme le dialog) */}
                  {!dialogError ? (
                    <button
                      onClick={handleAssocier}
                      disabled={associationLoading}
                      className="flex-1 py-2.5 text-sm font-bold text-white bg-green-600 rounded-xl hover:bg-green-700 disabled:opacity-50 transition flex items-center justify-center gap-2"
                    >
                      {associationLoading ? (
                        <RefreshCw size={16} className="animate-spin" />
                      ) : (
                        <>
                          <CheckCircle2 size={16} />
                          Associer
                        </>
                      )}
                    </button>
                  ) : dialogError.canForce ? (
                    <>
                      <button
                        onClick={() => { setDialogError(null); handleAssocier(); }}
                        disabled={associationLoading}
                        className="flex-1 py-2.5 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/40 rounded-xl hover:bg-blue-200 dark:hover:bg-blue-800/60 disabled:opacity-50 transition inline-flex items-center justify-center gap-2"
                      >
                        <RefreshCw size={16} /> Réessayer
                      </button>
                      <button
                        onClick={handleForceInDialog}
                        disabled={associationLoading}
                        className="flex-1 py-2.5 text-sm font-bold text-white bg-red-600 rounded-xl hover:bg-red-700 disabled:opacity-50 transition inline-flex items-center justify-center gap-2"
                        title="Passe outre la vérification et associe quand même"
                      >
                        {associationLoading ? <RefreshCw size={16} className="animate-spin" />
                          : <><AlertTriangle size={16} /> Forcer</>}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        setShowConfirmDialog(false); setConfirmAnimation('idle');
                        setDialogError(null); inputRef.current?.focus();
                      }}
                      className="flex-1 py-2.5 text-sm font-bold text-white bg-gray-600 rounded-xl hover:bg-gray-700 transition inline-flex items-center justify-center gap-2"
                    >
                      Fermer et corriger
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Scanner caméra */}
      {showCameraScanner && (
        <Suspense fallback={<div className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center"><div className="text-white">Chargement du scanner...</div></div>}>
          <BarcodeScanner
            onScan={(code) => {
              setCodeCopie(code);
              setShowCameraScanner(false);
              // Le useEffect sur codeCopie déclenchera automatiquement la recherche
            }}
            onClose={() => setShowCameraScanner(false)}
          />
        </Suspense>
      )}

      {/* Modal Attribution Formateurs */}
      {showAttribFormateur && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl mx-4 max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Attribution des Formateurs</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Attribuez un formateur aux copies associées par matière
                  {statsFormateur && (
                    <span className="ml-2 text-amber-600 font-medium">
                      {statsFormateur.total_sans_enseignant} copie(s) sans formateur
                    </span>
                  )}
                </p>
              </div>
              <button onClick={() => setShowAttribFormateur(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <XCircle size={22} />
              </button>
            </div>

            {/* Alertes : résumé des problèmes */}
            {statsFormateur && (() => {
              const nbAnomalies = statsFormateur.matieres.filter(m => m.classes?.some((c: any) => c.anomalie)).length;
              const nbConflits = statsFormateur.matieres.filter(m => m.enseignants.length > 1).length;
              const nbSansFormateur = statsFormateur.matieres.filter(m => m.sans_enseignant > 0).length;
              const totalProblemes = nbAnomalies + nbConflits;
              return totalProblemes > 0 ? (
                <div className="px-4 pt-3 pb-2 border-b border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-xs">
                      {nbConflits > 0 && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded font-medium">
                          <AlertTriangle size={12} /> {nbConflits} matiere(s) avec plusieurs formateurs
                        </span>
                      )}
                      {nbAnomalies > 0 && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded font-medium">
                          <AlertTriangle size={12} /> {nbAnomalies} avec classes anomaliques
                        </span>
                      )}
                      {nbSansFormateur > 0 && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded font-medium">
                          {nbSansFormateur} sans formateur
                        </span>
                      )}
                    </div>
                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                      <input type="checkbox" checked={attribFiltreProblemes} onChange={(e) => setAttribFiltreProblemes(e.target.checked)} className="rounded w-3.5 h-3.5 text-red-600" />
                      <span className="text-[10px] font-medium text-red-700 dark:text-red-300">Problemes uniquement</span>
                    </label>
                  </div>
                </div>
              ) : null;
            })()}

            {/* Filtre matière */}
            <div className="px-4 pt-3 pb-2 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <Search size={14} className="text-gray-400 shrink-0" />
                <input
                  type="text"
                  value={attribMatiereSearch}
                  onChange={(e) => setAttribMatiereSearch(e.target.value)}
                  placeholder="Filtrer par matiere, code ou classe..."
                  className="flex-1 px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
                {attribMatiereSearch && (
                  <button onClick={() => setAttribMatiereSearch('')} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {!statsFormateur ? (
                <div className="flex justify-center py-12"><RefreshCw size={24} className="animate-spin text-gray-400" /></div>
              ) : statsFormateur.matieres.length === 0 ? (
                <p className="text-center py-12 text-gray-400">Aucune copie associée trouvée.</p>
              ) : (
                <div className="space-y-2">
                  {statsFormateur.matieres.filter(mat => {
                    // Filtre recherche
                    if (attribMatiereSearch) {
                      const q = attribMatiereSearch.toLowerCase();
                      if (!mat.matiere_nom.toLowerCase().includes(q) && !mat.matiere_code.toLowerCase().includes(q) && !mat.classes?.some((c: any) => c.classe.toLowerCase().includes(q))) return false;
                    }
                    // Filtre problèmes uniquement
                    if (attribFiltreProblemes) {
                      const hasAnomalie = mat.classes?.some((c: any) => c.anomalie);
                      const hasConflit = mat.enseignants.length > 1;
                      const hasSansFormateur = mat.sans_enseignant > 0;
                      if (!hasAnomalie && !hasConflit && !hasSansFormateur) return false;
                    }
                    return true;
                  }).map((mat) => {
                    const hasConflit = mat.enseignants.length > 1;
                    const hasAnomalie = mat.classes?.some((c: any) => c.anomalie);
                    return (
                    <div key={mat.matiere_id} className={`border rounded-lg p-3 ${
                      hasConflit ? 'border-red-400 dark:border-red-700 bg-red-50/50 dark:bg-red-900/5' :
                      hasAnomalie ? 'border-orange-300 dark:border-orange-700 bg-orange-50/50 dark:bg-orange-900/5' :
                      mat.sans_enseignant > 0 ? 'border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/10' :
                      'border-gray-200 dark:border-gray-700'
                    }`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">{mat.matiere_nom}</span>
                            <span className="text-[10px] font-mono text-gray-400 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">{mat.matiere_code}</span>
                            <span className="text-xs text-gray-500">{mat.total} copie(s)</span>
                            {mat.sans_enseignant > 0 && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded font-medium">
                                {mat.sans_enseignant} sans formateur
                              </span>
                            )}
                            {hasConflit && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded font-medium animate-pulse">
                                ⚠ {mat.enseignants.length} formateurs — conflit
                              </span>
                            )}
                          </div>
                          {/* Alerte conflit formateurs */}
                          {hasConflit && (
                            <div className="mt-1 p-2 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded text-[10px]">
                              <div className="font-bold text-red-700 dark:text-red-300 mb-1">
                                ⚠ Conflit : cette matiere a {mat.enseignants.length} formateurs differents
                              </div>
                              <div className="text-red-600 dark:text-red-400 mb-1">
                                Une classe ne peut avoir qu'un seul formateur par matiere. Retirez les formateurs incorrects.
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {mat.enseignants.map((e) => (
                                  <span key={e.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-white dark:bg-gray-800 border border-red-300 dark:border-red-700 rounded">
                                    <span className="font-medium">{e.nom}</span>
                                    <span className="text-gray-400">({e.count} copie(s))</span>
                                    {(e.classes as any[])?.map((cl: any) => (
                                      <span key={cl.classe} className="px-1 bg-gray-100 dark:bg-gray-700 rounded text-[9px]">{cl.classe}</span>
                                    ))}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Classes — checkboxes avec détection anomalies */}
                          {mat.classes && mat.classes.length > 0 && (() => {
                            const allClasseNames = mat.classes.map((c: any) => c.classe);
                            const selected = selectedClassesForAttrib[mat.matiere_id] || allClasseNames;
                            return (
                              <div className="mt-1 space-y-1">
                                <div className="flex flex-wrap items-center gap-1.5">
                                  {mat.classes.map((c: any) => {
                                    const isChecked = selected.includes(c.classe);
                                    const isAnomalie = c.anomalie;
                                    return (
                                      <label
                                        key={c.classe}
                                        className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border cursor-pointer select-none transition-colors ${
                                          isAnomalie
                                            ? isChecked
                                              ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700'
                                              : 'bg-gray-100 dark:bg-gray-700 text-gray-400 border-gray-300 dark:border-gray-600 line-through'
                                            : isChecked
                                              ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700'
                                              : 'bg-gray-100 dark:bg-gray-700 text-gray-400 border-gray-300 dark:border-gray-600 line-through'
                                        }`}
                                        title={isAnomalie ? c.raison : isChecked ? 'Décocher pour exclure' : 'Cocher pour inclure'}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isChecked}
                                          onChange={async () => {
                                            if (!isChecked) {
                                              // Cocher → juste remettre dans la sélection
                                              setSelectedClassesForAttrib(prev => ({ ...prev, [mat.matiere_id]: [...selected, c.classe] }));
                                              return;
                                            }

                                            // Décocher → proposer de transférer vers une autre matière du même nom
                                            const autresMatieres = statsFormateur?.matieres.filter(
                                              m => m.matiere_nom === mat.matiere_nom && m.matiere_id !== mat.matiere_id
                                            ) || [];

                                            if (autresMatieres.length > 0) {
                                              // Construire le choix
                                              const choix = autresMatieres.map((m, i) =>
                                                `${i + 1}. ${m.matiere_code} (${m.classes?.map((cl: any) => cl.classe).join(', ')})${m.enseignants.length > 0 ? ` — ${m.enseignants[0].nom}` : ''}`
                                              ).join('\n');

                                              const reponse = window.prompt(
                                                `Transférer ${c.count} copie(s) de ${c.classe} vers quelle matière ?\n\n` +
                                                `Actuellement sur : ${mat.matiere_code}${mat.enseignants.length > 0 ? ` (${mat.enseignants[0].nom})` : ''}\n\n` +
                                                `Destinations possibles :\n${choix}\n\n` +
                                                `Tapez le numéro (1-${autresMatieres.length}), ou laissez vide pour juste décocher :`,
                                                '1'
                                              );

                                              if (reponse === null) return; // Annulé

                                              const idx = parseInt(reponse) - 1;
                                              if (reponse && !isNaN(idx) && idx >= 0 && idx < autresMatieres.length) {
                                                const dest = autresMatieres[idx];
                                                try {
                                                  const res = await transfererCopies({
                                                    source_matiere_id: mat.matiere_id,
                                                    dest_matiere_id: dest.matiere_id,
                                                    classes: [c.classe],
                                                    enseignant_id: dest.enseignants.length > 0 ? dest.enseignants[0].id : undefined,
                                                  }).unwrap();
                                                  setMessage({ type: 'success', text: `${res.message}` });
                                                  refetchStatsFormateur();
                                                } catch (err: any) {
                                                  setMessage({ type: 'error', text: err?.data?.error || 'Erreur de transfert' });
                                                }
                                              }
                                            }

                                            // Décocher dans tous les cas
                                            setSelectedClassesForAttrib(prev => ({
                                              ...prev, [mat.matiere_id]: selected.filter((cl: string) => cl !== c.classe)
                                            }));
                                          }}
                                          className="w-3 h-3 rounded border-gray-300 text-indigo-600 cursor-pointer"
                                        />
                                        {c.classe} <strong>{c.count}</strong>
                                        {isAnomalie && <span>⚠</span>}
                                      </label>
                                    );
                                  })}
                                  {selected.length < allClasseNames.length && (
                                    <span className="text-[9px] text-gray-400">{selected.length}/{allClasseNames.length}</span>
                                  )}
                                  {/* Bouton ajouter classe */}
                                  <div className="relative">
                                    <button
                                      type="button"
                                      onClick={() => { setAddClasseOpen(addClasseOpen === mat.matiere_id ? null : mat.matiere_id); setAddClasseSearch(''); }}
                                      className="text-[10px] px-1.5 py-0.5 rounded border border-dashed border-gray-400 dark:border-gray-500 text-gray-500 dark:text-gray-400 hover:border-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                                      title="Ajouter une classe"
                                    >
                                      + Classe
                                    </button>
                                    {addClasseOpen === mat.matiere_id && (
                                      <div className="absolute left-0 top-full mt-1 z-20 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-2">
                                        <input
                                          type="text"
                                          value={addClasseSearch}
                                          onChange={(e) => setAddClasseSearch(e.target.value)}
                                          placeholder="Rechercher une classe..."
                                          className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-400 mb-1.5"
                                          autoFocus
                                        />
                                        <div className="max-h-40 overflow-y-auto space-y-0.5">
                                          {allClassesBDD
                                            .filter((cl: any) => {
                                              const dejaPresent = allClasseNames.includes(cl.code) || selected.includes(cl.code);
                                              const matchSearch = !addClasseSearch || cl.code.toLowerCase().includes(addClasseSearch.toLowerCase()) || (cl.option_nom || '').toLowerCase().includes(addClasseSearch.toLowerCase());
                                              return !dejaPresent && matchSearch;
                                            })
                                            .slice(0, 15)
                                            .map((cl: any) => (
                                              <button
                                                key={cl.code}
                                                type="button"
                                                onClick={() => {
                                                  setSelectedClassesForAttrib(prev => ({
                                                    ...prev,
                                                    [mat.matiere_id]: [...selected, cl.code],
                                                  }));
                                                  setAddClasseOpen(null);
                                                }}
                                                className="w-full text-left px-2 py-1 text-[11px] rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/20 text-gray-700 dark:text-gray-300"
                                              >
                                                <span className="font-medium">{cl.code}</span>
                                                {cl.option_nom && <span className="text-gray-400 ml-1">— {cl.option_nom}</span>}
                                              </button>
                                            ))
                                          }
                                          {allClassesBDD.filter((cl: any) => {
                                            const dejaPresent = allClasseNames.includes(cl.code) || selected.includes(cl.code);
                                            return !dejaPresent && (!addClasseSearch || cl.code.toLowerCase().includes(addClasseSearch.toLowerCase()));
                                          }).length === 0 && (
                                            <div className="text-[10px] text-gray-400 text-center py-2">Aucune classe disponible</div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {/* Détail des anomalies */}
                                {mat.classes.filter((c: any) => c.anomalie).length > 0 && (
                                  <details className="text-[9px]">
                                    <summary className="cursor-pointer text-red-500 hover:text-red-700 dark:text-red-400 select-none font-medium">
                                      ⚠ {mat.classes.filter((c: any) => c.anomalie).length} classe(s) anomalique(s) — cliquer pour détails
                                    </summary>
                                    <div className="mt-1 p-2 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded space-y-1.5">
                                      {mat.classes.filter((c: any) => c.anomalie).map((c: any) => (
                                        <div key={c.classe}>
                                          <div className="font-semibold text-red-700 dark:text-red-300">{c.classe} — {c.count} copie(s) : {c.raison}</div>
                                          {c.etudiants && c.etudiants.length > 0 && (
                                            <div className="ml-2 text-red-600 dark:text-red-400">
                                              {c.etudiants.map((e: any) => (
                                                <div key={e.code_copie}>{e.code_copie} — {e.nom} ({e.matricule})</div>
                                              ))}
                                              {c.count > 5 && <div className="italic">... et {c.count - 5} autre(s)</div>}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                )}
                              </div>
                            );
                          })()}
                          {/* Formateurs existants — avec détail classes et total enveloppe */}
                          {mat.enseignants.length > 0 && (
                            <div className="space-y-1 mt-1.5">
                              {mat.enseignants.map((e) => (
                                <div key={e.id} className="flex items-center gap-2 px-2 py-1.5 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded group">
                                  <span className="text-[11px] font-semibold text-green-800 dark:text-green-300 shrink-0">{e.nom}</span>
                                  <span className="text-[10px] font-bold text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-1.5 py-0.5 rounded shrink-0">
                                    {e.count} copie(s)
                                  </span>
                                  {/* Classes du formateur */}
                                  {e.classes && (e.classes as any[]).length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                      {(e.classes as any[]).map((cl: any) => (
                                        <span key={cl.classe} className="text-[9px] px-1 py-0.5 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded">
                                          {cl.classe} <strong>{cl.count}</strong>
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  <button
                                    type="button"
                                    disabled={retirerLoading}
                                    onClick={async () => {
                                      if (!window.confirm(`Retirer ${e.nom} de ${mat.matiere_nom} (${e.count} copies) ?`)) return;
                                      try {
                                        const res = await retirerEnseignant({ matiere_id: mat.matiere_id, enseignant_id: e.id }).unwrap();
                                        setMessage({ type: 'success', text: res.message });
                                        refetchStatsFormateur();
                                      } catch (err: any) {
                                        setMessage({ type: 'error', text: err?.data?.error || 'Erreur' });
                                      }
                                    }}
                                    className="ml-auto text-green-400 hover:text-red-500 transition-colors opacity-50 group-hover:opacity-100 shrink-0"
                                    title={`Retirer ${e.nom}`}
                                  >
                                    ✗
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <select
                            id={`ens-${mat.matiere_id}`}
                            defaultValue={mat.enseignants[0]?.id || ''}
                            className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 max-w-[200px]"
                          >
                            <option value="">— Formateur —</option>
                            {formateurs.map((f: any) => (
                              <option key={f.id} value={f.id}>
                                {f.nom} {f.prenoms}
                              </option>
                            ))}
                          </select>
                          <button
                            disabled={attribLoading}
                            onClick={async () => {
                              const sel = document.getElementById(`ens-${mat.matiere_id}`) as HTMLSelectElement;
                              const ensId = sel?.value ? Number(sel.value) : null;
                              if (!ensId) { setMessage({ type: 'error', text: 'Sélectionnez un formateur' }); return; }
                              // Classes sélectionnées (toutes par défaut)
                              const allClasses = mat.classes?.map((c: any) => c.classe) || [];
                              const classes = selectedClassesForAttrib[mat.matiere_id] || allClasses;
                              if (classes.length === 0) { setMessage({ type: 'error', text: 'Sélectionnez au moins une classe' }); return; }
                              try {
                                const payload: any = { enseignant_id: ensId, matiere_id: mat.matiere_id };
                                if (classes.length < allClasses.length) payload.classes = classes;
                                const res = await attribuerEnseignant(payload).unwrap();
                                const classesLabel = classes.length < allClasses.length ? ` (${classes.join(', ')})` : '';
                                setMessage({ type: 'success', text: `${res.message}${classesLabel}` });
                                refetchStatsFormateur();
                              } catch (err: any) {
                                setMessage({ type: 'error', text: err?.data?.error || 'Erreur' });
                              }
                            }}
                            className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
                          >
                            Attribuer
                          </button>
                        </div>
                      </div>
                    </div>
                  ); })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes bounceIn {
          0% { transform: scale(0); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
        @keyframes loading-bar {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(0%); }
          100% { transform: translateX(100%); }
        }
        .animate-loading-bar {
          animation: loading-bar 1.5s ease-in-out infinite;
        }
        @keyframes progress-slide {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .animate-progress-slide {
          animation: progress-slide 1.8s linear infinite;
        }
      `}</style>

      {/* Modal Copies Sans Formateur */}
      {showSansFormateur && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-red-50 dark:bg-red-900/10">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <AlertTriangle size={20} className="text-red-500" />
                  Copies sans formateur attribué
                </h3>
                {dataSansFormateur && (
                  <div className="flex items-center gap-3 mt-1 text-xs">
                    <span className="font-semibold text-red-600 dark:text-red-400">
                      {dataSansFormateur.total_copies_sans_formateur} copie(s) — {dataSansFormateur.total_matieres} matière(s)
                    </span>
                    {dataSansFormateur.total_avec_suggestion > 0 && (
                      <span className="text-green-600 dark:text-green-400">
                        {dataSansFormateur.total_haute_confiance} suggestion(s) haute confiance
                      </span>
                    )}
                  </div>
                )}
              </div>
              <button onClick={() => setShowSansFormateur(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <XCircle size={22} />
              </button>
            </div>

            {/* Filtres */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Search size={14} className="text-gray-400" />
                <input
                  type="text"
                  value={sansFormateurSearch}
                  onChange={(e) => setSansFormateurSearch(e.target.value)}
                  placeholder="Rechercher matière, code, classe..."
                  className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-red-400 w-64"
                />
              </div>
              {dataSansFormateur && dataSansFormateur.niveaux.length > 0 && (
                <select
                  value={sansFormateurFiltre.niveau || ''}
                  onChange={(e) => setSansFormateurFiltre(prev => ({ ...prev, niveau: e.target.value || undefined }))}
                  className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">Tous niveaux</option>
                  {dataSansFormateur.niveaux.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              )}
              {dataSansFormateur && dataSansFormateur.poles.length > 0 && (
                <select
                  value={sansFormateurFiltre.pole || ''}
                  onChange={(e) => setSansFormateurFiltre(prev => ({ ...prev, pole: e.target.value || undefined }))}
                  className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">Tous pôles</option>
                  {dataSansFormateur.poles.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              )}
              <button
                onClick={() => refetchSansFormateur()}
                disabled={fetchingSansFormateur}
                className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <RefreshCw size={14} className={fetchingSansFormateur ? 'animate-spin' : ''} />
              </button>
            </div>

            {/* Contenu */}
            <div className="flex-1 overflow-y-auto p-4">
              {fetchingSansFormateur ? (
                <div className="flex justify-center py-12"><RefreshCw size={24} className="animate-spin text-gray-400" /></div>
              ) : !dataSansFormateur || dataSansFormateur.matieres.length === 0 ? (
                <div className="text-center py-16">
                  <CheckCircle2 size={48} className="mx-auto text-green-400 mb-3" />
                  <p className="text-lg font-medium text-gray-600 dark:text-gray-300">Toutes les copies ont un formateur !</p>
                  <p className="text-sm text-gray-400 mt-1">Aucune copie associée sans formateur attribué.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {dataSansFormateur.matieres
                    .filter(mat => {
                      if (!sansFormateurSearch) return true;
                      const q = sansFormateurSearch.toLowerCase();
                      return mat.matiere_nom.toLowerCase().includes(q)
                        || mat.matiere_code.toLowerCase().includes(q)
                        || mat.classes.some(c => c.classe.toLowerCase().includes(q))
                        || mat.niveau.toLowerCase().includes(q);
                    })
                    .map((mat) => {
                      const isExpanded = expandedMatiere === mat.matiere_id;
                      const bestSuggestion = mat.suggestions[0];
                      const hasHautConfiance = mat.suggestions.some(s => s.confiance === 'haute');
                      return (
                        <div key={mat.matiere_id} className={`border rounded-xl overflow-hidden transition-all ${
                          hasHautConfiance
                            ? 'border-green-300 dark:border-green-700 bg-green-50/30 dark:bg-green-900/5'
                            : mat.suggestions.length > 0
                              ? 'border-amber-300 dark:border-amber-700 bg-amber-50/30 dark:bg-amber-900/5'
                              : 'border-red-300 dark:border-red-700 bg-red-50/30 dark:bg-red-900/5'
                        }`}>
                          {/* En-tête matière */}
                          <div
                            className="px-4 py-3 cursor-pointer hover:bg-gray-50/50 dark:hover:bg-gray-700/20 transition-colors"
                            onClick={() => setExpandedMatiere(isExpanded ? null : mat.matiere_id)}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                {isExpanded ? <ChevronDown size={16} className="text-gray-400 shrink-0" /> : <ChevronRight size={16} className="text-gray-400 shrink-0" />}
                                <span className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">{mat.matiere_nom}</span>
                                <span className="text-[10px] font-mono text-gray-400 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded shrink-0">{mat.matiere_code}</span>
                                <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded shrink-0">{mat.niveau}</span>
                                {mat.option && <span className="text-[10px] text-gray-400 shrink-0">{mat.option}</span>}
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="text-xs font-bold text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30 px-2 py-0.5 rounded-full">
                                  {mat.nb_copies} copie(s)
                                </span>
                                {/* Classes badges */}
                                <div className="flex gap-1">
                                  {mat.classes.slice(0, 3).map(c => (
                                    <span key={c.classe} className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                                      {c.classe} ({c.count})
                                    </span>
                                  ))}
                                  {mat.classes.length > 3 && <span className="text-[10px] text-gray-400">+{mat.classes.length - 3}</span>}
                                </div>
                                {/* Suggestion rapide */}
                                {bestSuggestion && (
                                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                                    bestSuggestion.confiance === 'haute'
                                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                      : bestSuggestion.confiance === 'moyenne'
                                        ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                                  }`}>
                                    {bestSuggestion.confiance === 'haute' ? '★' : bestSuggestion.confiance === 'moyenne' ? '◐' : '○'} {bestSuggestion.nom}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Détails expandés */}
                          {isExpanded && (
                            <div className="px-4 pb-4 border-t border-gray-200 dark:border-gray-700 pt-3 space-y-3">
                              {/* Suggestions de formateurs */}
                              <div>
                                <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
                                  <TrendingUp size={13} />
                                  Suggestions de formateur
                                  {mat.suggestions.length === 0 && <span className="text-red-500 font-normal ml-1">— Aucune suggestion disponible</span>}
                                </h4>
                                {mat.suggestions.length > 0 ? (
                                  <div className="space-y-1.5">
                                    {mat.suggestions.map((s) => (
                                      <div key={s.id} className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border ${
                                        s.confiance === 'haute'
                                          ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
                                          : s.confiance === 'moyenne'
                                            ? 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800'
                                            : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                                      }`}>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2">
                                            <span className={`text-xs px-1.5 py-0.5 rounded font-bold ${
                                              s.confiance === 'haute' ? 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200'
                                              : s.confiance === 'moyenne' ? 'bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200'
                                              : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                                            }`}>
                                              {s.confiance === 'haute' ? 'Haute' : s.confiance === 'moyenne' ? 'Moyenne' : 'Basse'}
                                            </span>
                                            <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">{s.nom}</span>
                                          </div>
                                          <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                                            {s.source === 'attribution_officielle' && 'Attribution officielle dans le programme'}
                                            {s.source === 'meme_matiere' && (s.detail || 'Formateur déjà sur des copies de cette matière')}
                                            {s.source === 'autre_option' && (s.detail || 'Enseigne cette matière dans une autre option')}
                                            {s.classes_communes && s.classes_communes.length > 0 && (
                                              <span className="ml-1 text-green-600 dark:text-green-400">
                                                — classes communes : {s.classes_communes.join(', ')}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                        <button
                                          disabled={attribLoading}
                                          onClick={async () => {
                                            try {
                                              const res = await attribuerEnseignant({
                                                enseignant_id: s.id,
                                                matiere_id: mat.matiere_id,
                                              }).unwrap();
                                              setMessage({ type: 'success', text: `${s.nom} attribué à ${mat.matiere_nom} — ${res.nb_mises_a_jour} copie(s)` });
                                              refetchSansFormateur();
                                            } catch (err: any) {
                                              setMessage({ type: 'error', text: err?.data?.error || 'Erreur d\'attribution' });
                                            }
                                          }}
                                          className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap shrink-0"
                                        >
                                          Attribuer
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                                    <Info size={14} className="text-gray-400" />
                                    <span className="text-xs text-gray-500">Sélectionnez manuellement un formateur :</span>
                                    <select
                                      id={`sf-ens-${mat.matiere_id}`}
                                      className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 flex-1 max-w-[250px]"
                                    >
                                      <option value="">— Formateur —</option>
                                      {formateurs.map((f: any) => (
                                        <option key={f.id} value={f.id}>{f.nom} {f.prenoms}</option>
                                      ))}
                                    </select>
                                    <button
                                      disabled={attribLoading}
                                      onClick={async () => {
                                        const sel = document.getElementById(`sf-ens-${mat.matiere_id}`) as HTMLSelectElement;
                                        const ensId = sel?.value ? Number(sel.value) : null;
                                        if (!ensId) { setMessage({ type: 'error', text: 'Sélectionnez un formateur' }); return; }
                                        try {
                                          const res = await attribuerEnseignant({ enseignant_id: ensId, matiere_id: mat.matiere_id }).unwrap();
                                          setMessage({ type: 'success', text: res.message });
                                          refetchSansFormateur();
                                        } catch (err: any) {
                                          setMessage({ type: 'error', text: err?.data?.error || 'Erreur' });
                                        }
                                      }}
                                      className="px-3 py-1 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                    >
                                      Attribuer
                                    </button>
                                  </div>
                                )}
                                {/* Sélection manuelle même quand il y a des suggestions */}
                                {mat.suggestions.length > 0 && (
                                  <div className="mt-2 flex items-center gap-2 text-[10px] text-gray-400">
                                    <span>Autre formateur :</span>
                                    <select
                                      id={`sf-ens-manual-${mat.matiere_id}`}
                                      className="px-2 py-1 text-[10px] border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 max-w-[200px]"
                                    >
                                      <option value="">— Choisir —</option>
                                      {formateurs
                                        .filter((f: any) => !mat.suggestions.some(s => s.id === f.id))
                                        .map((f: any) => (
                                          <option key={f.id} value={f.id}>{f.nom} {f.prenoms}</option>
                                        ))}
                                    </select>
                                    <button
                                      disabled={attribLoading}
                                      onClick={async () => {
                                        const sel = document.getElementById(`sf-ens-manual-${mat.matiere_id}`) as HTMLSelectElement;
                                        const ensId = sel?.value ? Number(sel.value) : null;
                                        if (!ensId) return;
                                        try {
                                          const res = await attribuerEnseignant({ enseignant_id: ensId, matiere_id: mat.matiere_id }).unwrap();
                                          setMessage({ type: 'success', text: res.message });
                                          refetchSansFormateur();
                                        } catch (err: any) {
                                          setMessage({ type: 'error', text: err?.data?.error || 'Erreur' });
                                        }
                                      }}
                                      className="px-2 py-0.5 text-[10px] bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50"
                                    >
                                      OK
                                    </button>
                                  </div>
                                )}
                              </div>

                              {/* Liste des étudiants */}
                              <div>
                                <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
                                  <Users size={13} />
                                  Étudiants concernés ({mat.etudiants.length})
                                </h4>
                                <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                                  <table className="min-w-full text-xs">
                                    <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0">
                                      <tr>
                                        <th className="px-3 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Code copie</th>
                                        <th className="px-3 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Matricule</th>
                                        <th className="px-3 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Nom & Prénoms</th>
                                        <th className="px-3 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Classe</th>
                                        <th className="px-3 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Pôle</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                      {mat.etudiants.map((etu) => (
                                        <tr key={etu.copie_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                          <td className="px-3 py-1.5 font-mono text-gray-600 dark:text-gray-400">{etu.code_copie}</td>
                                          <td className="px-3 py-1.5 font-medium text-gray-900 dark:text-gray-100">{etu.matricule}</td>
                                          <td className="px-3 py-1.5 text-gray-900 dark:text-gray-100">{etu.nom} {etu.prenoms}</td>
                                          <td className="px-3 py-1.5">
                                            <span className="px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded text-[10px]">{etu.classe}</span>
                                          </td>
                                          <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">{etu.pole || '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>

                              {/* Traçabilité */}
                              {mat.associes_par.length > 0 && (
                                <div className="text-[10px] text-gray-400 flex items-center gap-2">
                                  <Clock size={11} />
                                  Associées par : {mat.associes_par.map(a => `${a.username} (${a.count})`).join(', ')}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>

            {/* Footer avec actions groupées */}
            {dataSansFormateur && dataSansFormateur.total_haute_confiance > 0 && (
              <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-green-50 dark:bg-green-900/10 flex items-center justify-between">
                <span className="text-xs text-green-700 dark:text-green-300">
                  <TrendingUp size={14} className="inline mr-1" />
                  {dataSansFormateur.total_haute_confiance} matière(s) avec suggestion haute confiance
                </span>
                <button
                  disabled={attribLoading}
                  onClick={async () => {
                    const matieresHaute = dataSansFormateur.matieres.filter(m => m.suggestions.some(s => s.confiance === 'haute'));
                    if (!window.confirm(`Attribuer automatiquement les formateurs haute confiance pour ${matieresHaute.length} matière(s) ?`)) return;
                    let success = 0;
                    let errors = 0;
                    for (const m of matieresHaute) {
                      const bestSugg = m.suggestions.find(s => s.confiance === 'haute');
                      if (!bestSugg) continue;
                      try {
                        await attribuerEnseignant({ enseignant_id: bestSugg.id, matiere_id: m.matiere_id }).unwrap();
                        success++;
                      } catch {
                        errors++;
                      }
                    }
                    setMessage({ type: success > 0 ? 'success' : 'error', text: `${success} attribution(s) réussie(s)${errors > 0 ? `, ${errors} erreur(s)` : ''}` });
                    refetchSansFormateur();
                  }}
                  className="px-4 py-2 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  Attribuer tout (haute confiance)
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal d'avertissement incoherence */}
      {warningData && (
        <WarningModal
          data={warningData}
          onForce={handleForceAssociation}
          onCancel={() => {
            setWarningData(null);
            setMessage({ type: 'error', text: `Association annulee : ${warningData.details?.etudiant} (${warningData.details?.classe}) ne correspond pas a la matiere` });
          }}
        />
      )}

      {/* Modal de dissociation en lot par matière + formateur */}
      <DissocierLotModal
        open={showDissocierLot}
        onClose={() => setShowDissocierLot(false)}
        onSuccess={(msg) => {
          setMessage({ type: 'success', text: msg });
          refetchStats();
          refetchAssociees();
          refetchHistorique();
        }}
      />

      {/* Modal Progression Association vs EDT — suivi journalier attendus/associés/écart */}
      <ProgressionEDTModal
        isOpen={showProgressionEDT}
        onClose={() => setShowProgressionEDT(false)}
        defaultTypeExamen={sessionMode === 'RATTRAPAGE' ? 'RATTRAPAGE' : 'NORMAL'}
      />

      {/* Modal Transfert de matière — ré-attribuer toutes les copies d'une matière vers une autre */}
      <TransfererMatiereModal
        isOpen={showTransfererMatiere}
        onClose={() => setShowTransfererMatiere(false)}
        onSuccess={(msg) => {
          setMessage({ type: 'success', text: msg });
          refetchStats();
          refetchAssociees();
          refetchHistorique();
        }}
      />
    </div>
  );
};

// Modal d'avertissement d'incoherence
const WarningModal: React.FC<{
  data: any;
  onForce: () => void;
  onCancel: () => void;
}> = ({ data, onForce, onCancel }) => {
  if (!data) return null;
  const d = data.details || {};
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Header rouge */}
        <div className="bg-red-600 px-6 py-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <AlertTriangle size={24} className="text-white" />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">Incoherence detectee</h3>
            <p className="text-red-100 text-xs">L'association est possible mais necessite votre confirmation</p>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Etudiant */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
            <div className="text-xs text-gray-500 uppercase font-medium mb-1">Etudiant</div>
            <div className="font-semibold text-gray-900 dark:text-gray-100">{d.etudiant}</div>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs font-mono text-gray-500">{d.matricule}</span>
              {d.classe && <span className="text-xs px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded font-medium">{d.classe}</span>}
              {d.option && <span className="text-xs text-gray-400">{d.option}</span>}
            </div>
          </div>

          {/* Matiere */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
            <div className="text-xs text-gray-500 uppercase font-medium mb-1">Matiere selectionnee</div>
            <div className="font-semibold text-gray-900 dark:text-gray-100">{d.matiere}</div>
            <div className="flex items-center gap-2 mt-1">
              {d.matiere_code && <span className="text-xs font-mono text-gray-500">{d.matiere_code}</span>}
              {d.niveau_matiere && <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-medium">Niveau {d.niveau_matiere}</span>}
            </div>
          </div>

          {/* Explication du probleme */}
          <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <div className="text-xs font-bold text-red-700 dark:text-red-300 mb-1">Probleme detecte :</div>
            <div className="text-sm text-red-600 dark:text-red-400">
              {d.niveau_etudiant && d.niveau_matiere && d.niveau_etudiant !== d.niveau_matiere ? (
                <>
                  L'etudiant est en <strong>{d.classe}</strong> (niveau <strong>{d.niveau_etudiant}</strong>),
                  mais la matiere <strong>{d.matiere}</strong> est de niveau <strong>{d.niveau_matiere}</strong>.
                  <br /><br />
                  <span className="text-xs text-red-500">
                    Cela signifie que la copie <strong>{d.code_copie}</strong> se trouve probablement dans la <strong>mauvaise enveloppe</strong>.
                    Verifiez physiquement l'enveloppe avant de forcer.
                  </span>
                </>
              ) : (
                <>
                  La matiere <strong>{d.matiere}</strong> ne figure pas dans le programme
                  de <strong>{d.classe}</strong> ({d.option}).
                  <br /><br />
                  <span className="text-xs text-red-500">
                    Verifiez que l'etudiant est bien inscrit dans cette classe et que la copie
                    <strong> {d.code_copie}</strong> appartient bien a cette enveloppe.
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Comparaison */}
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded p-2">
              <div className="font-bold text-green-700 dark:text-green-300 mb-1">Ce qui est attendu</div>
              <div className="text-green-600 dark:text-green-400">
                Un etudiant de niveau <strong>{d.niveau_matiere || '?'}</strong> inscrit dans une classe
                qui a la matiere "{d.matiere}" dans son programme.
              </div>
            </div>
            <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded p-2">
              <div className="font-bold text-red-700 dark:text-red-300 mb-1">Ce qui est detecte</div>
              <div className="text-red-600 dark:text-red-400">
                L'etudiant <strong>{d.etudiant}</strong> est en <strong>{d.classe}</strong>
                {d.niveau_etudiant !== d.niveau_matiere ? ` (niveau ${d.niveau_etudiant})` : ''},
                cette matiere n'est pas dans son programme.
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 flex items-center justify-between gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium text-sm hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            Annuler (recommande)
          </button>
          <button
            onClick={onForce}
            className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium text-sm hover:bg-red-700"
          >
            Forcer l'association
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssociationCopies;
